/* ============================================================
   MA Quiz Wizard
   - Theme-editor-configurable quiz/wizard
   - State machine: steps → answers → result rule engine
   - Adds matched variant + optional add-on items to cart via /cart/add.js
   - Emits: maQuizAnswer, maQuizComplete, maQuizCartAdded
   - Smart resume: remembered steps skipped on return visits
   - Phase 4: transitions, a11y, error/success states, progress titles
   - Phase 5: collection_step — product grid with sort, filter, infinite scroll,
               qty controls, variant picker, and product detail modal
   ============================================================ */

(function () {
  'use strict';

  const MA_QUIZ_DEBUG = true; // set to true to log allocation + pricing data

  class MAQuizWizard extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this._storageKey = `ma-quiz-wizard-remembered-${this.sectionId}`;

      const dataEl = document.getElementById(`quiz-wizard-data-${this.sectionId}`);
      if (!dataEl) return;

      let parsed;
      try {
        parsed = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[MAQuizWizard] Failed to parse block data:', e);
        return;
      }

      this._steps = parsed.steps || [];
      this._answers = parsed.answers || [];
      const planDbg = this._answers.filter(a => a.answer_type === 'plan');
      console.log('[GiftDebug] plan answers:', planDbg.map(a => ({ value: a.value, g1: a.free_gift_variant_id, g2: a.free_gift_2_variant_id })));
      const rawEl = document.getElementById(`quiz-wizard-data-${this.sectionId}`);
      const rawQ = (parsed.answers || []).find(a => a.value === 'quarterly');
      console.log('[RawData] sectionId:', this.sectionId, '| quarterly g2 raw:', rawQ?.free_gift_2_variant_id, '| element:', rawEl?.id);
      this._results = parsed.results || [];
      this._allocations = parsed.allocations || [];
      this._variants = parsed.variants || [];
      this._moneyFormat = parsed.money_format || '${{amount}}';
      this._startOverLabel = parsed.start_over_label || 'Start Over';
      this._defaultVariantId = parsed.default_variant_id || null;
      this._kickers = {
        message: parsed.kicker_message || 'PERSONALIZE',
        collection: parsed.kicker_collection || 'ADD-ONS',
        plan: parsed.kicker_plan || 'CHOOSE YOUR PLAN',
        box_size: parsed.kicker_box_size || 'BOX SIZE',
        quiz: parsed.kicker_quiz || 'QUIZ',
      };
      this._cartGifts = (parsed.cart_gifts || []).filter((g) => g.enabled && g.variant_id);

      if (MA_QUIZ_DEBUG) console.log('[MAQuizWizard] allocations:', this._allocations);

      if (!this._steps.length) return;

      // ── Build lookup maps ──────────────────────────────────
      this._stepMap = {};
      this._steps.forEach((s) => {
        this._stepMap[s.step_id] = s;
      });

      this._answerMap = {};
      this._answers.forEach((a) => {
        if (!this._answerMap[a.step_id]) this._answerMap[a.step_id] = [];
        this._answerMap[a.step_id].push(a);
      });

      // ── State ─────────────────────────────────────────────
      this._state = {
        currentStepId: this._steps[0].step_id,
        history: [],
        selections: {},
        addons: {}, // key: `${productId}-${variantId}`, value: { product_id, variant_id, quantity, title }
        giftMessages: {}, // key: step_id, value: { recipient, message }
        completed: false,
        adding: false,
      };

      this._stepsContainer = this.querySelector('.ma-quiz-wizard__steps');
      this._successPanel = this.querySelector('.ma-quiz-wizard__success');
      this._progressFill = document.querySelector('.ma-quiz-wizard__progress-fill');
      this._btnBack = this.querySelector('.ma-quiz-wizard__btn-back');
      this._headerBackBtn = document.querySelector('.ma-quiz-header__back');
      this._btnNext = this.querySelector('.ma-quiz-wizard__btn-next');
      this._btnFinish = this.querySelector('.ma-quiz-wizard__btn-finish');
      this._btnStartOver = this.querySelector('.ma-quiz-wizard__btn-start-over');
      this._selectionCountEl = this.querySelector('.ma-quiz-wizard__selection-count');
      this._errorEl = this.querySelector('.ma-quiz-wizard__error');

      // Capture original button labels for dynamic CTA text
      this._nextLabel = this._btnNext?.textContent.trim() || 'Next';
      this._finishLabel = this._btnFinish?.textContent.trim() || 'Add to Cart';

      // Mirror back label onto the header back button (shows "← Back" via ::before + text)
      if (this._headerBackBtn) {
        const backLabel = this._btnBack?.textContent.trim() || 'Back';
        this._headerBackBtn.textContent = backLabel;
      }

      // ── Shared product detail modal (appended to body to avoid stacking issues) ──
      this._modal = this._buildModalContainer();
      document.body.appendChild(this._modal);

      // ── Sticky bottom bar for collection steps ──
      this._stickyBar = this._buildStickyBar();
      document.body.appendChild(this._stickyBar);
      this._initStickyScroll();

      this._loadRememberedSelections();
      this._render();
      this._bindNav();

      // Reset loading state when browser restores page from bfcache (back button)
      this._pagesShowHandler = (e) => {
        if (!e.persisted) return;
        if (this._btnFinish) {
          this._btnFinish.classList.remove('is-loading');
          this._btnFinish.disabled = false;
        }
        this._state.adding = false;
      };
      window.addEventListener('pageshow', this._pagesShowHandler);
    }

    disconnectedCallback() {
      if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
      if (this._pagesShowHandler) window.removeEventListener('pageshow', this._pagesShowHandler);
      this._modal?.parentNode?.removeChild(this._modal);
      this._stickyBar?.parentNode?.removeChild(this._stickyBar);
      if (this._headerBackBtn) this._headerBackBtn.hidden = true;
      // Clean up per-step sticky toolbars and their observers
      Object.values(this._stepMap || {}).forEach((s) => {
        if (s._toolbarIO) { s._toolbarIO.disconnect(); s._toolbarIO = null; }
        if (s._stickyToolbar) { s._stickyToolbar.remove(); s._stickyToolbar = null; }
      });
    }

    // ── Smart resume ──────────────────────────────────────────────

    _saveRememberedSelections() {
      const toSave = {};
      this._steps.forEach((step) => {
        if (step.remember_answer && this._state.selections[step.step_id]) {
          toSave[step.step_id] = this._state.selections[step.step_id];
        }
      });
      try {
        localStorage.setItem(this._storageKey, JSON.stringify(toSave));
      } catch (_) {}
    }

    _loadRememberedSelections() {
      let saved = {};
      try {
        const raw = localStorage.getItem(this._storageKey);
        if (raw) saved = JSON.parse(raw);
      } catch (_) {}

      this._steps.forEach((step) => {
        if (!step.remember_answer || !saved[step.step_id]) return;
        // Restore skip sentinel as-is without answer validation
        if (saved[step.step_id][0] === '__skipped__') {
          this._state.selections[step.step_id] = ['__skipped__'];
          return;
        }
        const validValues = (this._answerMap[step.step_id] || []).map((a) => a.value);
        const validSaved = saved[step.step_id].filter((v) => validValues.includes(v));
        if (validSaved.length > 0) this._state.selections[step.step_id] = validSaved;
      });

      this._applyResumeSkip();
    }

    // Shared skip logic — walks the step flow respecting next_step_override
    _applyResumeSkip() {
      let currentId = this._steps[0].step_id;
      const history = [];

      while (currentId) {
        const step = this._stepMap[currentId];
        const hasAnswer = (this._state.selections[currentId] || []).length > 0;

        if (step?.remember_answer && hasAnswer) {
          history.push(currentId);
          const nextId = this._getNextStep(currentId);
          if (!nextId) break;
          currentId = nextId;
        } else {
          break;
        }
      }

      this._state.currentStepId = currentId;
      this._state.history = history;
    }

    // ── Rendering ─────────────────────────────────────────────────

    _render() {
      this._renderSteps();
      this._updateProgress();
      this._updateNav();
    }

    _renderSteps() {
      if (!this._stepsContainer) return;
      this._stepsContainer.innerHTML = '';
      this._steps.forEach((step) => {
        this._stepsContainer.appendChild(this._buildStepPanel(step));
      });
      this._activateStep(this._state.currentStepId, false);
      this._steps.forEach((s) => this._updateTileStates(s.step_id));
    }

    // ── Plan pricing helpers ───────────────────────────────────────

    _getActiveVariantId() {
      for (let i = this._state.history.length - 1; i >= 0; i--) {
        const stepId = this._state.history[i];
        const selected = this._state.selections[stepId] || [];
        const answers = this._answerMap[stepId] || [];
        for (const val of selected) {
          const answer = answers.find((a) => a.value === val);
          if (answer?.shopify_variant_id) return answer.shopify_variant_id;
        }
      }
      return this._defaultVariantId;
    }

    _getAllocation(variantId, sellingPlanId) {
      return (
        this._allocations.find(
          (a) => String(a.variant_id) === String(variantId) && String(a.plan_id) === String(sellingPlanId),
        ) || null
      );
    }

    _getAllocationByName(variantId, planName) {
      const normalizedPlanName = (planName || '').trim();
      const match = this._allocations.find(
        (a) => String(a.variant_id) === String(variantId) && a.plan_name.trim() === normalizedPlanName
      ) || null;
      if (MA_QUIZ_DEBUG) {
        const variantPlans = this._allocations.filter((a) => String(a.variant_id) === String(variantId)).map((a) => a.plan_name);
        console.log('[MAQuizWizard] _getAllocationByName', { variantId, planName: normalizedPlanName, match: !!match, availablePlans: variantPlans });
      }
      return match;
    }

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return (this._moneyFormat || '${{amount}}')
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)))
        .replace('{{amount_with_apostrophe_separator}}', amount.replace('.', "'"));
    }

    _minVariantPrice(variantId) {
      let min = Infinity;
      for (const answers of Object.values(this._answerMap)) {
        for (const answer of answers) {
          if (answer.answer_type !== 'plan' || !answer.plan_name_match) continue;
          const entry = this._getAllocationByName(variantId, answer.plan_name_match);
          if (!entry) continue;
          const perMonth = Math.round(entry.price / (answer.months || 1));
          if (perMonth < min) min = perMonth;
        }
      }
      return min === Infinity ? null : this._formatMoney(min);
    }

    _getStepKicker(step) {
      if (step.step_type === 'message') return this._kickers.message;
      if (step.step_type === 'collection') return this._kickers.collection;
      const answers = this._answerMap[step.step_id] || [];
      const allPlan = answers.length > 0 && answers.every((a) => a.answer_type === 'plan');
      if (allPlan) return this._kickers.plan;
      const hasImages = answers.some((a) => a.image_url);
      if (hasImages) return this._kickers.box_size;
      return this._kickers.quiz;
    }

    _buildStepPanel(step) {
      const headingId = `quiz-heading-${this.sectionId}-${step.step_id}`;

      const panel = document.createElement('div');
      panel.className = 'ma-quiz-wizard__step';
      panel.dataset.stepId = step.step_id;
      if (step.auto_advance) panel.dataset.autoAdvance = 'true';

      // Message step: special background class + envelope icon
      if (step.step_type === 'message') {
        panel.classList.add('ma-quiz-wizard__step--message');
        const envelope = document.createElement('div');
        envelope.className = 'ma-quiz-wizard__message-envelope';
        envelope.setAttribute('aria-hidden', 'true');
        envelope.textContent = '💌';
        panel.appendChild(envelope);
      }

      // Step kicker
      const kicker = document.createElement('div');
      kicker.className = 'ma-quiz-wizard__step-kicker';
      kicker.setAttribute('aria-hidden', 'true');
      kicker.textContent = this._getStepKicker(step);
      panel.appendChild(kicker);

      const question = document.createElement('h2');
      question.className = 'ma-quiz-wizard__question';
      question.id = headingId;
      question.setAttribute('tabindex', '-1');
      question.textContent = step.question || '';
      panel.appendChild(question);

      if (step.subtitle) {
        const sub = document.createElement('p');
        sub.className = 'ma-quiz-wizard__subtitle';
        sub.textContent = step.subtitle;
        panel.appendChild(sub);
      }

      if (step.step_type === 'collection') {
        panel.appendChild(this._buildCollectionPanel(step));
      } else if (step.step_type === 'message') {
        panel.appendChild(this._buildMessagePanel(step));
      } else {
        const inputType = step.input_type || 'radio';
        const stepAnswers = this._answerMap[step.step_id] || [];
        const allPlan = stepAnswers.length > 0 && stepAnswers.every((a) => a.answer_type === 'plan');

        // Binary layout: exactly 2 non-plan answers (skip for addon_mode — uses full-width stacked cards)
        const isBinary = !allPlan && !step.addon_mode && stepAnswers.length === 2;

        const grid = document.createElement('div');
        grid.setAttribute('role', inputType === 'checkbox' ? 'group' : 'radiogroup');
        grid.setAttribute('aria-labelledby', headingId);

        if (isBinary) {
          grid.className = 'ma-quiz-wizard__tiles ma-quiz-wizard__tiles--binary';
          grid.appendChild(this._buildBinaryTile(step, stepAnswers[0], true));
          grid.appendChild(this._buildBinaryTile(step, stepAnswers[1], false));
        } else {
          grid.className = 'ma-quiz-wizard__tiles' + (allPlan ? ' ma-quiz-wizard__tiles--plan' : '');
          if (!allPlan) {
            const cols = step.addon_mode ? 1 : (step.desktop_columns || 2);
            const colsMobile = step.addon_mode ? 1 : (step.mobile_columns || 1);
            grid.style.setProperty('--ma-cols-desktop', cols);
            grid.style.setProperty('--ma-cols-mobile', colsMobile);
          }
          stepAnswers.forEach((answer) => {
            grid.appendChild(this._buildTile(step, answer));
          });
        }

        panel.appendChild(grid);
      }

      // Skip button lives inside the step panel, below tiles
      if (step.skippable) {
        const skipWrap = document.createElement('div');
        skipWrap.className = 'ma-quiz-wizard__step-skip-wrap';
        const skipBtn = document.createElement('button');
        skipBtn.type = 'button';
        skipBtn.className = 'ma-quiz-wizard__step-skip';
        skipBtn.textContent = step.skip_label || 'Skip';
        skipBtn.addEventListener('click', () => this._skip());
        skipWrap.appendChild(skipBtn);
        panel.appendChild(skipWrap);
      }

      return panel;
    }

    _buildBinaryTile(step, answer, isYes) {
      const inputType = step.input_type || 'radio';
      const tile = document.createElement('div');
      tile.className = isYes
        ? 'ma-quiz-wizard__tile ma-quiz-wizard__tile--binary-yes'
        : 'ma-quiz-wizard__tile ma-quiz-wizard__tile--binary-no';
      tile.dataset.stepId = step.step_id;
      tile.dataset.value = answer.value;
      tile.setAttribute('role', inputType === 'checkbox' ? 'checkbox' : 'radio');
      tile.setAttribute('aria-checked', 'false');
      tile.setAttribute('tabindex', '0');
      tile.setAttribute('aria-label', answer.label || answer.value);

      // Image area
      const imgArea = document.createElement('div');
      imgArea.className = 'ma-quiz-wizard__binary-image';
      imgArea.setAttribute('aria-hidden', 'true');
      if (answer.image_url) {
        const img = document.createElement('img');
        img.src = answer.image_url;
        img.alt = '';
        img.loading = 'lazy';
        imgArea.appendChild(img);
      } else {
        const { emoji } = this._extractEmoji(answer.label || '');
        imgArea.textContent = emoji || (isYes ? '✓' : '✗');
      }
      tile.appendChild(imgArea);

      // Content area
      const content = document.createElement('div');
      content.className = 'ma-quiz-wizard__binary-content';
      content.setAttribute('aria-hidden', 'true');

      if (isYes) {
        const badge = document.createElement('div');
        badge.className = 'ma-quiz-wizard__binary-badge';
        badge.textContent = '★ RECOMMENDED';
        content.appendChild(badge);
      }

      const { emoji: _e, rest: labelText } = this._extractEmoji(answer.label || answer.value);
      const labelEl = document.createElement('div');
      labelEl.className = 'ma-quiz-wizard__binary-label';
      labelEl.textContent = labelText || answer.label || answer.value;
      content.appendChild(labelEl);

      if (answer.description) {
        const desc = document.createElement('div');
        desc.className = 'ma-quiz-wizard__binary-desc';
        desc.textContent = answer.description;
        content.appendChild(desc);
      }

      tile.appendChild(content);

      // Check circle
      const check = document.createElement('span');
      check.className = 'ma-quiz-wizard__binary-check';
      check.setAttribute('aria-hidden', 'true');
      tile.appendChild(check);

      tile.addEventListener('click', () => this._onTileClick(step.step_id, answer.value));
      tile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._onTileClick(step.step_id, answer.value);
        }
      });

      return tile;
    }

    _extractEmoji(str) {
      const match = str.match(/^(\p{Regional_Indicator}{2}|\p{Emoji_Presentation}|\p{Extended_Pictographic})\s*/u);
      if (match) return { emoji: match[0].trim(), rest: str.slice(match[0].length).trim() };
      return { emoji: '', rest: str };
    }

    _buildTile(step, answer) {
      if (answer.answer_type === 'plan') return this._buildPlanTile(step, answer);
      if (step.addon_mode) return this._buildAddonTile(step, answer);
      const inputType = step.input_type || 'radio';
      const hasImage = !!answer.image_url;

      const tile = document.createElement('div');
      tile.className = 'ma-quiz-wizard__tile';
      tile.dataset.stepId = step.step_id;
      tile.dataset.value = answer.value;
      tile.dataset.label = answer.label || answer.value;
      tile.setAttribute('role', inputType === 'checkbox' ? 'checkbox' : 'radio');
      tile.setAttribute('aria-checked', 'false');
      tile.setAttribute('tabindex', '0');
      tile.setAttribute('aria-label', answer.label || answer.value);

      if (answer.badge_text) {
        const badge = document.createElement('span');
        badge.className = 'ma-quiz-wizard__tile-badge' + (answer.badge_style === 'dark' ? ' ma-quiz-wizard__tile-badge--dark' : '');
        badge.textContent = answer.badge_text;
        tile.appendChild(badge);
      }

      if (hasImage) {
        tile.classList.add('ma-quiz-wizard__tile--image');
        const imgWrap = document.createElement('div');
        imgWrap.className = 'ma-quiz-wizard__tile-image-wrap';
        imgWrap.setAttribute('aria-hidden', 'true');
        const img = document.createElement('img');
        img.className = 'ma-quiz-wizard__tile-image';
        img.src = answer.image_url;
        img.alt = '';
        img.loading = 'lazy';
        imgWrap.appendChild(img);
        tile.appendChild(imgWrap);

        const body = document.createElement('div');
        body.className = 'ma-quiz-wizard__tile-body';
        body.setAttribute('aria-hidden', 'true');

        // Split "5-7 Snacks" → count "5-7" + descriptor "Snacks"
        const rawLabel = answer.label || answer.value;
        const labelParts = rawLabel.match(/^([\d\-–]+)\s+(.+)$/);
        if (labelParts) {
          const countRow = document.createElement('div');
          countRow.className = 'ma-quiz-wizard__tile-count-row';
          const countEl = document.createElement('span');
          countEl.className = 'ma-quiz-wizard__tile-count';
          countEl.textContent = labelParts[1];
          const descriptorEl = document.createElement('span');
          descriptorEl.className = 'ma-quiz-wizard__tile-descriptor';
          descriptorEl.textContent = labelParts[2];
          countRow.appendChild(countEl);
          countRow.appendChild(descriptorEl);
          body.appendChild(countRow);
        } else {
          const label = document.createElement('p');
          label.className = 'ma-quiz-wizard__tile-label';
          label.textContent = rawLabel;
          body.appendChild(label);
        }

        // Split description "Standard Box as low as $17.99/box" → tier + price
        if (answer.description) {
          const priceMatch = answer.description.match(/^(.+?)\s+(as low as .+)$/i);
          if (priceMatch) {
            const tierEl = document.createElement('p');
            tierEl.className = 'ma-quiz-wizard__tile-tier';
            tierEl.textContent = priceMatch[1];
            body.appendChild(tierEl);
            const priceEl = document.createElement('p');
            priceEl.className = 'ma-quiz-wizard__tile-price';
            const livePrice = answer.shopify_variant_id
              ? this._minVariantPrice(answer.shopify_variant_id)
              : null;
            priceEl.textContent = livePrice ? `as low as ${livePrice}/box` : priceMatch[2];
            body.appendChild(priceEl);
          } else {
            const desc = document.createElement('p');
            desc.className = 'ma-quiz-wizard__tile-description';
            desc.textContent = answer.description;
            body.appendChild(desc);
          }
        }

        // Select pill (desktop only — hidden on mobile via CSS)
        const pill = document.createElement('div');
        pill.className = 'ma-quiz-wizard__tile-select-pill';
        pill.setAttribute('aria-hidden', 'true');
        pill.textContent = 'Choose this box';
        body.appendChild(pill);
        tile.appendChild(body);
      } else {
        tile.classList.add('ma-quiz-wizard__tile--no-image');

        // Emoji circle
        const { emoji, rest } = this._extractEmoji(answer.label || answer.value);
        const emojiEl = document.createElement('span');
        emojiEl.className = 'ma-quiz-wizard__tile-emoji';
        emojiEl.setAttribute('aria-hidden', 'true');
        emojiEl.textContent = emoji;
        tile.appendChild(emojiEl);

        const body = document.createElement('div');
        body.className = 'ma-quiz-wizard__tile-body';
        body.setAttribute('aria-hidden', 'true');
        const label = document.createElement('p');
        label.className = 'ma-quiz-wizard__tile-label';
        label.textContent = rest || answer.label || answer.value;
        body.appendChild(label);
        if (answer.description) {
          const desc = document.createElement('p');
          desc.className = 'ma-quiz-wizard__tile-description';
          desc.textContent = answer.description;
          body.appendChild(desc);
        }
        tile.appendChild(body);

        // Check at end of flex row
        const check = document.createElement('span');
        check.className = 'ma-quiz-wizard__tile-check';
        check.setAttribute('aria-hidden', 'true');
        check.dataset.kind = inputType;
        tile.appendChild(check);
      }

      tile.addEventListener('click', () => this._onTileClick(step.step_id, answer.value));
      tile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._onTileClick(step.step_id, answer.value);
        }
      });

      return tile;
    }

    _buildAddonTile(step, answer) {
      const rawLabel = answer.label || answer.value;
      const { emoji, rest } = this._extractEmoji(rawLabel);
      const labelText = rest || rawLabel;

      const tile = document.createElement('div');
      tile.className = 'ma-quiz-wizard__tile ma-quiz-wizard__tile--addon';
      tile.dataset.stepId = step.step_id;
      tile.dataset.value = answer.value;
      tile.dataset.label = rawLabel;
      tile.setAttribute('role', 'checkbox');
      tile.setAttribute('aria-checked', 'false');
      tile.setAttribute('tabindex', '0');
      tile.setAttribute('aria-label', rawLabel);

      const imgArea = document.createElement('div');
      imgArea.className = 'ma-quiz-wizard__binary-image';
      imgArea.setAttribute('aria-hidden', 'true');
      if (answer.image_url) {
        const img = document.createElement('img');
        img.src = answer.image_url;
        img.alt = '';
        img.loading = 'lazy';
        imgArea.appendChild(img);
      } else {
        imgArea.textContent = emoji;
      }
      tile.appendChild(imgArea);

      const body = document.createElement('div');
      body.className = 'ma-quiz-wizard__tile-body';
      body.setAttribute('aria-hidden', 'true');
      const label = document.createElement('p');
      label.className = 'ma-quiz-wizard__tile-label';
      label.textContent = labelText;
      body.appendChild(label);
      if (answer.description) {
        const desc = document.createElement('p');
        desc.className = 'ma-quiz-wizard__tile-description';
        desc.textContent = answer.description;
        body.appendChild(desc);
      }
      tile.appendChild(body);

      const btn = document.createElement('span');
      btn.className = 'ma-quiz-wizard__tile-addon-btn';
      btn.setAttribute('aria-hidden', 'true');
      btn.textContent = 'Upgrade Box';
      tile.appendChild(btn);

      tile.addEventListener('click', () => this._onTileClick(step.step_id, answer.value));
      tile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._onTileClick(step.step_id, answer.value); }
      });

      return tile;
    }

    _buildPlanTile(step, answer) {
      const inputType = step.input_type || 'radio';
      const variantId = this._getActiveVariantId();
      const entry = variantId && answer.plan_name_match ? this._getAllocationByName(variantId, answer.plan_name_match) : null;

      const tile = document.createElement('div');
      tile.className = 'ma-quiz-wizard__tile ma-quiz-wizard__tile--plan';
      tile.dataset.stepId = step.step_id;
      tile.dataset.value = answer.value;
      tile.dataset.label = answer.label || answer.value;
      if (answer.plan_name_match && !entry) tile.hidden = true;
      tile.setAttribute('role', inputType === 'checkbox' ? 'checkbox' : 'radio');
      tile.setAttribute('aria-checked', 'false');
      tile.setAttribute('tabindex', '0');

      // DS: left border stripe (shown when selected via CSS)
      const stripe = document.createElement('div');
      stripe.className = 'ma-quiz-wizard__plan-stripe';
      stripe.setAttribute('aria-hidden', 'true');
      tile.appendChild(stripe);

      // DS: radio circle on left
      const radio = document.createElement('span');
      radio.className = 'ma-quiz-wizard__plan-radio';
      radio.setAttribute('aria-hidden', 'true');
      tile.appendChild(radio);

      if (answer.badge_text) {
        const badge = document.createElement('span');
        badge.className = 'ma-quiz-wizard__tile-badge' + (answer.badge_style === 'dark' ? ' ma-quiz-wizard__tile-badge--dark' : '');
        badge.textContent = answer.badge_text;
        tile.appendChild(badge);
      }

      const body = document.createElement('div');
      body.className = 'ma-quiz-wizard__plan-body';
      body.setAttribute('aria-hidden', 'true');

      const planName = document.createElement('div');
      planName.className = 'ma-quiz-wizard__plan-name';
      planName.textContent = answer.label || (entry ? entry.plan_name : answer.value);
      body.appendChild(planName);

      if (entry) {
        const months = answer.months || 1;
        const perMonth = Math.round(entry.price / months);
        // compare_at_price = undiscounted per-month price set by Recharge — use as savings baseline
        const baseline = entry.compare_at_price || null;
        const savings = baseline && baseline > perMonth ? Math.round(((baseline - perMonth) / baseline) * 100) : 0;
        const isMulti = months > 1;

        const labelParts = [answer.label || entry.plan_name, this._formatMoney(perMonth) + '/mo'];
        if (isMulti) labelParts.push(this._formatMoney(entry.price) + ' billed total');
        if (savings > 0) labelParts.push('Save ' + savings + '%');
        if (answer.free_gift_label && this._isPlanGiftEligible(answer, true)) labelParts.push(answer.free_gift_label);
        tile.setAttribute('aria-label', labelParts.join(', '));

        // Right column: price + billed total (grouped for mobile right-align)
        const planRight = document.createElement('div');
        planRight.className = 'ma-quiz-wizard__plan-right';

        const pricing = document.createElement('div');
        pricing.className = 'ma-quiz-wizard__plan-pricing';

        const perMonthEl = document.createElement('span');
        perMonthEl.className = 'ma-quiz-wizard__plan-per-month';
        perMonthEl.appendChild(document.createTextNode(this._formatMoney(perMonth)));
        const moTag = document.createElement('small');
        moTag.textContent = '/mo';
        perMonthEl.appendChild(moTag);
        pricing.appendChild(perMonthEl);

        if (savings > 0 && baseline) {
          const compare = document.createElement('span');
          compare.className = 'ma-quiz-wizard__plan-compare';
          compare.textContent = this._formatMoney(baseline);
          pricing.appendChild(compare);
        }

        planRight.appendChild(pricing);
        body.appendChild(planRight);

        // Meta row: savings badge + billed total side-by-side (one row on mobile, col on desktop)
        if (savings > 0 || isMulti) {
          const meta = document.createElement('div');
          meta.className = 'ma-quiz-wizard__plan-meta';

          if (savings > 0) {
            const savingsEl = document.createElement('span');
            savingsEl.className = 'ma-quiz-wizard__plan-savings';
            savingsEl.textContent = 'Save ' + savings + '%';
            meta.appendChild(savingsEl);
          }

          if (isMulti) {
            const total = document.createElement('span');
            total.className = 'ma-quiz-wizard__plan-total';
            total.textContent = this._formatMoney(entry.price) + ' billed total';
            meta.appendChild(total);
          }

          body.appendChild(meta);
        }
      } else {
        const noEntryGifts = this._getEligibleGifts(answer, null, true);
        const ariaLabel = noEntryGifts.length
          ? `${answer.label || answer.value}, ${noEntryGifts.length} free gift${noEntryGifts.length > 1 ? 's' : ''} included`
          : answer.label || answer.value;
        tile.setAttribute('aria-label', ariaLabel);
      }

      const gifts = this._getEligibleGifts(answer, entry, true);
      if (gifts.length) {
        const prevLabel = tile.getAttribute('aria-label') || answer.label || answer.value;
        tile.setAttribute('aria-label', `${prevLabel}, ${gifts.length} free gift${gifts.length > 1 ? 's' : ''} included`);

        const trigger = document.createElement('div');
        trigger.className = 'ma-quiz-wizard__plan-gift-trigger';
        trigger.setAttribute('aria-hidden', 'true');

        const thumbStack = document.createElement('div');
        thumbStack.className = 'ma-quiz-wizard__plan-gift-thumbs';
        gifts.forEach((g) => {
          const thumb = document.createElement('div');
          thumb.className = 'ma-quiz-wizard__plan-gift-thumb';
          if (g.image_url) {
            const img = document.createElement('img');
            img.src = g.image_url;
            img.alt = '';
            thumb.appendChild(img);
          } else {
            thumb.textContent = '🎁';
          }
          thumbStack.appendChild(thumb);
        });
        trigger.appendChild(thumbStack);

        const countEl = document.createElement('span');
        countEl.className = 'ma-quiz-wizard__plan-gift-count';
        countEl.textContent = `+ ${gifts.length} Free Gift${gifts.length > 1 ? 's' : ''}`;
        trigger.appendChild(countEl);

        const detailsBtn = document.createElement('button');
        detailsBtn.type = 'button';
        detailsBtn.className = 'ma-quiz-wizard__plan-gift-details-btn';
        detailsBtn.textContent = 'Details →';
        detailsBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._openGiftOverlay(answer.label || (entry ? entry.plan_name : answer.value), gifts);
        });
        trigger.appendChild(detailsBtn);

        body.appendChild(trigger);
      }

      tile.appendChild(body);

      tile.addEventListener('click', () => this._onTileClick(step.step_id, answer.value));
      tile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._onTileClick(step.step_id, answer.value);
        }
      });

      return tile;
    }

    _buildMessagePanel(step) {
      const grid = document.createElement('div');
      grid.className = 'ma-quiz-wizard__message-grid';

      const wrap = document.createElement('div');
      wrap.className = 'ma-quiz-wizard__message-form';

      const current = this._state.giftMessages[step.step_id] || {};

      // Recipient field
      const recipientGroup = document.createElement('div');
      recipientGroup.className = 'ma-quiz-wizard__field-group';

      const recipientLabel = document.createElement('label');
      recipientLabel.className = 'ma-quiz-wizard__field-label';
      recipientLabel.htmlFor = `msg-recipient-${this.sectionId}-${step.step_id}`;
      recipientLabel.textContent = step.recipient_label || "Recipient's Name";
      if (step.recipient_required) {
        const req = document.createElement('span');
        req.className = 'ma-quiz-wizard__field-required';
        req.setAttribute('aria-hidden', 'true');
        req.textContent = ' *';
        recipientLabel.appendChild(req);
      }

      const recipientInput = document.createElement('input');
      recipientInput.type = 'text';
      recipientInput.id = `msg-recipient-${this.sectionId}-${step.step_id}`;
      recipientInput.className = 'ma-quiz-wizard__field-input';
      recipientInput.placeholder = step.recipient_placeholder || '';
      recipientInput.value = current.recipient || '';
      if (step.recipient_required) recipientInput.setAttribute('aria-required', 'true');

      recipientGroup.appendChild(recipientLabel);
      recipientGroup.appendChild(recipientInput);
      wrap.appendChild(recipientGroup);

      // Message field
      const messageGroup = document.createElement('div');
      messageGroup.className = 'ma-quiz-wizard__field-group';

      const messageLabel = document.createElement('label');
      messageLabel.className = 'ma-quiz-wizard__field-label';
      messageLabel.htmlFor = `msg-message-${this.sectionId}-${step.step_id}`;
      messageLabel.textContent = step.message_label || 'Your Message';
      if (step.message_required) {
        const req = document.createElement('span');
        req.className = 'ma-quiz-wizard__field-required';
        req.setAttribute('aria-hidden', 'true');
        req.textContent = ' *';
        messageLabel.appendChild(req);
      }

      const messageTextarea = document.createElement('textarea');
      messageTextarea.id = `msg-message-${this.sectionId}-${step.step_id}`;
      messageTextarea.className = 'ma-quiz-wizard__field-textarea';
      messageTextarea.placeholder = step.message_placeholder || '';
      messageTextarea.rows = 4;
      messageTextarea.value = current.message || '';
      if (step.message_required) messageTextarea.setAttribute('aria-required', 'true');
      if (step.message_max_length) {
        messageTextarea.maxLength = step.message_max_length;
        const counterId = `msg-counter-${this.sectionId}-${step.step_id}`;
        messageTextarea.setAttribute('aria-describedby', counterId);
      }

      messageGroup.appendChild(messageLabel);
      messageGroup.appendChild(messageTextarea);

      if (step.message_max_length) {
        const counter = document.createElement('div');
        counter.className = 'ma-quiz-wizard__field-counter';
        counter.id = `msg-counter-${this.sectionId}-${step.step_id}`;
        counter.setAttribute('aria-live', 'polite');
        counter.textContent = `${(current.message || '').length} / ${step.message_max_length}`;
        messageGroup.appendChild(counter);
      }

      wrap.appendChild(messageGroup);

      // Gift card preview
      const preview = document.createElement('div');
      preview.className = 'ma-quiz-wizard__gift-card-preview';

      const ribbon = document.createElement('div');
      ribbon.className = 'ma-quiz-wizard__gift-card-ribbon';
      preview.appendChild(ribbon);

      const overline = document.createElement('div');
      overline.className = 'ma-quiz-wizard__gift-preview-overline';
      overline.textContent = 'MUNCH ADDICT';
      preview.appendChild(overline);

      const previewName = document.createElement('div');
      previewName.className = 'ma-quiz-wizard__gift-preview-name';
      previewName.textContent = current.recipient || '';
      preview.appendChild(previewName);

      const previewMsg = document.createElement('div');
      previewMsg.className = 'ma-quiz-wizard__gift-preview-message';
      previewMsg.textContent = current.message || '';
      preview.appendChild(previewMsg);

      // Sync to state on every keystroke
      const syncState = () => {
        if (!this._state.giftMessages[step.step_id]) this._state.giftMessages[step.step_id] = {};
        this._state.giftMessages[step.step_id].recipient = recipientInput.value;
        this._state.giftMessages[step.step_id].message = messageTextarea.value;
        if (step.message_max_length) {
          const counter = document.getElementById(`msg-counter-${this.sectionId}-${step.step_id}`);
          if (counter) counter.textContent = `${messageTextarea.value.length} / ${step.message_max_length}`;
        }
        // Live preview update
        previewName.textContent = recipientInput.value;
        previewMsg.textContent = messageTextarea.value;
        this._updateNav();
      };

      recipientInput.addEventListener('input', syncState);
      messageTextarea.addEventListener('input', syncState);

      grid.appendChild(wrap);
      grid.appendChild(preview);
      return grid;
    }

    _buildOrderNote() {
      const parts = [];
      for (const step of this._steps) {
        if (step.step_type !== 'message') continue;
        const vals = this._state.giftMessages[step.step_id] || {};
        const recipient = (vals.recipient || '').trim();
        const message = (vals.message || '').trim();
        if (!recipient && !message) continue;
        const template = (step.note_template || 'Gift To: {recipient}\nMessage: {message}').trim();
        if (!template) continue;
        parts.push(template.replace('{recipient}', recipient).replace('{message}', message));
      }
      return parts.join('\n\n');
    }

    _refreshPlanTiles(stepId) {
      const answers = this._answerMap[stepId] || [];
      if (!answers.some((a) => a.answer_type === 'plan')) return;

      const panel = this._stepsContainer.querySelector(`.ma-quiz-wizard__step[data-step-id="${stepId}"]`);
      if (!panel) return;
      const grid = panel.querySelector('.ma-quiz-wizard__tiles');
      if (!grid) return;

      const step = this._stepMap[stepId];
      grid.innerHTML = '';
      answers.forEach((answer) => grid.appendChild(this._buildTile(step, answer)));
      this._updateTileStates(stepId);
    }

    _activateStep(stepId, focusHeading = true) {
      this._stepsContainer.querySelectorAll('.ma-quiz-wizard__step').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.stepId === stepId);
      });

      // Hide all sticky toolbars; the IntersectionObserver will show the right one if needed
      Object.values(this._stepMap).forEach((s) => {
        if (s._stickyToolbar) s._stickyToolbar.hidden = true;
      });

      this._refreshPlanTiles(stepId);

      // Async-fetch all collection products on first activation
      const step = this._stepMap[stepId];
      if (step?.step_type === 'collection') {
        this._hideStickyBar(); // always start collapsed; scroll handler re-shows it
        if (step.collection_handle && !step._fetchStarted) {
          this._fetchCollectionProducts(step);
        }
      }

      if (focusHeading) {
        const panel = this._stepsContainer.querySelector(`.ma-quiz-wizard__step[data-step-id="${stepId}"]`);
        const heading = panel?.querySelector('.ma-quiz-wizard__question');
        if (heading) requestAnimationFrame(() => heading.focus());
      }
    }

    // ── Async collection product fetch ────────────────────────────

    _fetchCollectionProducts(step) {
      step._fetchStarted = true;

      const grid = this._stepsContainer.querySelector(`.ma-quiz-wizard__addon-grid[data-step-id="${step.step_id}"]`);
      if (!grid) return;

      const wrap = grid.closest('.ma-quiz-wizard__addon-wrap');
      const loadingEl = document.createElement('div');
      loadingEl.className = 'ma-quiz-wizard__addon-loading';
      loadingEl.innerHTML = '<span class="ma-quiz-wizard__addon-spinner" aria-hidden="true"></span> Loading products…';
      if (wrap) wrap.appendChild(loadingEl);

      this._fetchAllCollectionProducts(step.collection_handle)
        .then((products) => {
          step.collection_products = products;
          step._loadedCount = 0;
          loadingEl.remove();
          const sentinel = wrap?.querySelector('.ma-quiz-wizard__addon-sentinel');
          if (grid && sentinel) this._refreshAddonGrid(step, grid, sentinel);
        })
        .catch(() => {
          loadingEl.remove();
        });
    }

    async _fetchAllCollectionProducts(handle) {
      const all = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await fetch(`/collections/${handle}?view=quiz-wizard-json&page=${page}`);
        if (!res.ok) break;
        const data = await res.json();
        if (Array.isArray(data.products)) all.push(...data.products);
        totalPages = data.pages || 1;
        page++;
      } while (page <= totalPages);
      return all;
    }

    // ── Collection step ───────────────────────────────────────────

    _buildCollectionPanel(step) {
      const wrap = document.createElement('div');
      wrap.className = 'ma-quiz-wizard__addon-wrap';

      const products = step.collection_products || [];
      step._sortOrder = 'featured';
      step._activeFilterTag = '';
      step._loadedCount = 0;
      step._displayProducts = [...products];

      // Toolbar
      const hasSort = step.show_sorting;
      const hasFilter = step.filter_options && step.filter_options.length > 0;

      if (hasSort || hasFilter) {
        const toolbar = document.createElement('div');
        toolbar.className = 'ma-quiz-wizard__addon-toolbar';

        // Sort pill (custom styled trigger over hidden native select)
        if (hasSort) {
          const sortWrap = document.createElement('div');
          sortWrap.className = 'ma-quiz-wizard__addon-sort-wrap';

          const sortPill = document.createElement('div');
          sortPill.className = 'ma-quiz-wizard__addon-sort-pill';
          sortPill.innerHTML =
            '<span class="ma-quiz-wizard__addon-sort-label">SORT</span>' +
            '<span class="ma-quiz-wizard__addon-sort-val">Featured</span>' +
            '<span class="ma-quiz-wizard__addon-sort-arrow">&#9660;</span>';

          const sortSelect = document.createElement('select');
          sortSelect.className = 'ma-quiz-wizard__addon-sort';
          sortSelect.setAttribute('aria-label', 'Sort products');
          [
            ['featured', 'Featured'],
            ['best_selling', 'Best Selling'],
            ['price_asc', 'Price ↑'],
            ['price_desc', 'Price ↓'],
            ['title_asc', 'A – Z'],
            ['title_desc', 'Z – A'],
          ].forEach(([val, lbl]) => {
            const opt = document.createElement('option');
            opt.value = val;
            opt.textContent = lbl;
            sortSelect.appendChild(opt);
          });
          sortSelect.addEventListener('change', () => {
            step._sortOrder = sortSelect.value;
            const valEl = sortWrap.querySelector('.ma-quiz-wizard__addon-sort-val');
            if (valEl) valEl.textContent = sortSelect.options[sortSelect.selectedIndex].text;
            this._syncToolbarSelects(step);
            this._refreshAddonGrid(step, grid, sentinel);
          });
          step._sortSelect = sortSelect;
          step._sortWrap = sortWrap;

          sortWrap.appendChild(sortPill);
          sortWrap.appendChild(sortSelect);
          toolbar.appendChild(sortWrap);
        }

        // Item count (inline on desktop, wraps to own line on mobile via CSS)
        const countEl = document.createElement('span');
        countEl.className = 'ma-quiz-wizard__addon-count';
        countEl.textContent = products.length + ' items';
        step._countEl = countEl;
        toolbar.appendChild(countEl);

        // Vertical divider (hidden on mobile via CSS)
        const divider = document.createElement('div');
        divider.className = 'ma-quiz-wizard__addon-divider';
        toolbar.appendChild(divider);

        // Chip rail with scrollable chips + fade overlay
        if (hasFilter) {
          const railWrap = document.createElement('div');
          railWrap.className = 'ma-quiz-wizard__addon-chip-rail';

          const chipsWrap = document.createElement('div');
          chipsWrap.className = 'ma-quiz-wizard__addon-chips';

          // Enable horizontal scroll via mouse wheel + drag on desktop
          chipsWrap.addEventListener('wheel', (e) => {
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
            e.preventDefault();
            chipsWrap.scrollLeft += e.deltaY;
          }, { passive: false });
          this._addDragScroll(chipsWrap);

          const makeChip = (tag, label) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'ma-quiz-wizard__addon-chip' + (tag === '' ? ' is-active' : '');
            chip.dataset.tag = tag;
            chip.textContent = label;
            chip.addEventListener('click', () => {
              step._activeFilterTag = tag;
              this._syncToolbarSelects(step);
              this._refreshAddonGrid(step, grid, sentinel);
            });
            return chip;
          };

          chipsWrap.appendChild(makeChip('', 'All'));
          step.filter_options.forEach(({ tag, label }) => chipsWrap.appendChild(makeChip(tag, label)));

          step._chipsWrap = chipsWrap;

          const fade = document.createElement('div');
          fade.className = 'ma-quiz-wizard__addon-chip-fade';

          railWrap.appendChild(chipsWrap);
          railWrap.appendChild(fade);
          toolbar.appendChild(railWrap);
        }

        wrap.appendChild(toolbar);

        // Sticky toolbar is built after grid/sentinel are defined (below)
        step._mainToolbar = toolbar;
        step._hasToolbar = true;
      }

      // Grid
      const grid = document.createElement('div');
      grid.className = 'ma-quiz-wizard__addon-grid';
      grid.dataset.stepId = step.step_id;
      wrap.appendChild(grid);

      // Sentinel for IntersectionObserver
      const sentinel = document.createElement('div');
      sentinel.className = 'ma-quiz-wizard__addon-sentinel';
      wrap.appendChild(sentinel);

      // Load More button — only created in button mode
      step._loadStyle = step.load_more_style || 'infinite';
      step._loadMoreBtn = null;
      if (step._loadStyle === 'button') {
        const loadMoreBtn = document.createElement('button');
        loadMoreBtn.type = 'button';
        loadMoreBtn.className = 'ma-quiz-wizard__addon-load-more';
        loadMoreBtn.textContent = 'Load More';
        loadMoreBtn.hidden = true;
        loadMoreBtn.addEventListener('click', () => {
          this._renderAddonBatch(step, grid, step.products_per_page || 8);
          loadMoreBtn.hidden = step._loadedCount >= step._displayProducts.length;
        });
        wrap.appendChild(loadMoreBtn);
        step._loadMoreBtn = loadMoreBtn;
      }

      // Store grid/sentinel on step for use in sticky toolbar callbacks
      step._grid = grid;
      step._sentinel = sentinel;

      // Build sticky toolbar now that grid and sentinel exist
      if (step._hasToolbar) {
        const stickyBar = this._buildStickyToolbar(step);
        document.body.appendChild(stickyBar);
        step._stickyToolbar = stickyBar;

        // Show sticky bar only when main toolbar has scrolled above the fold
        step._toolbarIO = new IntersectionObserver(([entry]) => {
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          stickyBar.hidden = !scrolledPast;
        }, { threshold: 0 });
        step._toolbarIO.observe(step._mainToolbar);
      }

      // Initial render
      this._refreshAddonGrid(step, grid, sentinel);

      return wrap;
    }

    _buildStickyToolbar(step) {
      const bar = document.createElement('div');
      bar.className = 'ma-quiz-wizard__sticky-toolbar';
      bar.hidden = true;

      const inner = document.createElement('div');
      inner.className = 'ma-quiz-wizard__sticky-toolbar-inner page-width';

      const hasFilter = step.filter_options && step.filter_options.length > 0;
      const hasSort = step.show_sorting;
      const products = step.collection_products || [];

      if (hasSort) {
        const sortWrap = document.createElement('div');
        sortWrap.className = 'ma-quiz-wizard__addon-sort-wrap';

        const sortPill = document.createElement('div');
        sortPill.className = 'ma-quiz-wizard__addon-sort-pill';
        sortPill.innerHTML =
          '<span class="ma-quiz-wizard__addon-sort-label">SORT</span>' +
          '<span class="ma-quiz-wizard__addon-sort-val">Featured</span>' +
          '<span class="ma-quiz-wizard__addon-sort-arrow">&#9660;</span>';

        const sel = document.createElement('select');
        sel.className = 'ma-quiz-wizard__addon-sort';
        sel.setAttribute('aria-label', 'Sort products');
        [
          ['featured', 'Featured'],
          ['best_selling', 'Best Selling'],
          ['price_asc', 'Price ↑'],
          ['price_desc', 'Price ↓'],
          ['title_asc', 'A – Z'],
          ['title_desc', 'Z – A'],
        ].forEach(([val, lbl]) => {
          const opt = document.createElement('option');
          opt.value = val;
          opt.textContent = lbl;
          sel.appendChild(opt);
        });
        sel.addEventListener('change', () => {
          step._sortOrder = sel.value;
          const valEl = sortWrap.querySelector('.ma-quiz-wizard__addon-sort-val');
          if (valEl) valEl.textContent = sel.options[sel.selectedIndex].text;
          this._syncToolbarSelects(step);
          this._refreshAddonGrid(step, step._grid, step._sentinel);
        });
        step._stickySortSelect = sel;
        step._stickySortWrap = sortWrap;

        sortWrap.appendChild(sortPill);
        sortWrap.appendChild(sel);
        inner.appendChild(sortWrap);
      }

      // Item count
      const countEl = document.createElement('span');
      countEl.className = 'ma-quiz-wizard__addon-count';
      countEl.textContent = products.length + ' items';
      step._stickyCountEl = countEl;
      inner.appendChild(countEl);

      // Divider
      const divider = document.createElement('div');
      divider.className = 'ma-quiz-wizard__addon-divider';
      inner.appendChild(divider);

      if (hasFilter) {
        const railWrap = document.createElement('div');
        railWrap.className = 'ma-quiz-wizard__addon-chip-rail';

        const chipsWrap = document.createElement('div');
        chipsWrap.className = 'ma-quiz-wizard__addon-chips';

        // Enable horizontal scroll via mouse wheel + drag on desktop
        chipsWrap.addEventListener('wheel', (e) => {
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
          e.preventDefault();
          chipsWrap.scrollLeft += e.deltaY;
        }, { passive: false });
        this._addDragScroll(chipsWrap);

        const makeChip = (tag, label) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'ma-quiz-wizard__addon-chip' + (tag === '' ? ' is-active' : '');
          chip.dataset.tag = tag;
          chip.textContent = label;
          chip.addEventListener('click', () => {
            step._activeFilterTag = tag;
            this._syncToolbarSelects(step);
            this._refreshAddonGrid(step, step._grid, step._sentinel);
          });
          return chip;
        };

        chipsWrap.appendChild(makeChip('', 'All'));
        step.filter_options.forEach(({ tag, label }) => chipsWrap.appendChild(makeChip(tag, label)));

        step._stickyChipsWrap = chipsWrap;

        const fade = document.createElement('div');
        fade.className = 'ma-quiz-wizard__addon-chip-fade';

        railWrap.appendChild(chipsWrap);
        railWrap.appendChild(fade);
        inner.appendChild(railWrap);
      }

      bar.appendChild(inner);
      return bar;
    }

    _addDragScroll(el) {
      let startX, startLeft, dragging = false;
      el.addEventListener('mousedown', (e) => {
        dragging = true;
        startX = e.pageX;
        startLeft = el.scrollLeft;
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
      });
      const onMove = (e) => {
        if (!dragging) return;
        el.scrollLeft = startLeft - (e.pageX - startX);
      };
      const onUp = () => {
        dragging = false;
        el.style.cursor = '';
        el.style.userSelect = '';
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }

    _syncToolbarSelects(step) {
      const sort = step._sortOrder || 'featured';
      const tag = step._activeFilterTag || '';

      // Sync native selects
      if (step._sortSelect) {
        step._sortSelect.value = sort;
        const valEl = step._sortWrap && step._sortWrap.querySelector('.ma-quiz-wizard__addon-sort-val');
        if (valEl) valEl.textContent = step._sortSelect.options[step._sortSelect.selectedIndex].text;
      }
      if (step._stickySortSelect) {
        step._stickySortSelect.value = sort;
        const valEl = step._stickySortWrap && step._stickySortWrap.querySelector('.ma-quiz-wizard__addon-sort-val');
        if (valEl) valEl.textContent = step._stickySortSelect.options[step._stickySortSelect.selectedIndex].text;
      }

      // Sync chip active states
      [step._chipsWrap, step._stickyChipsWrap].forEach((wrap) => {
        if (!wrap) return;
        wrap.querySelectorAll('.ma-quiz-wizard__addon-chip').forEach((chip) => {
          chip.classList.toggle('is-active', chip.dataset.tag === tag);
        });
      });
    }

    _refreshAddonGrid(step, grid, sentinel) {
      const products = step.collection_products || [];
      const perPage = step.products_per_page || 8;

      // Only show available products
      const availableProducts = products.filter((p) => p.available !== false);

      // Filter by selected category tag
      const activeTag = (step._activeFilterTag || '').toLowerCase();
      let filtered = activeTag
        ? availableProducts.filter((p) => (p.tags || []).some((t) => t.toLowerCase() === activeTag))
        : [...availableProducts];

      // Sort (featured / best_selling = original collection order, no client-side re-sort)
      const sort = step._sortOrder || 'featured';
      if (sort === 'price_asc') filtered.sort((a, b) => a.price - b.price);
      else if (sort === 'price_desc') filtered.sort((a, b) => b.price - a.price);
      else if (sort === 'title_asc') filtered.sort((a, b) => a.title.localeCompare(b.title));
      else if (sort === 'title_desc') filtered.sort((a, b) => b.title.localeCompare(a.title));

      step._displayProducts = filtered;
      step._loadedCount = 0;
      grid.innerHTML = '';

      // Update item count elements
      const countText = filtered.length + ' items';
      if (step._countEl) step._countEl.textContent = countText;
      if (step._stickyCountEl) step._stickyCountEl.textContent = countText;

      // Disconnect previous observer
      if (step._observer) {
        step._observer.disconnect();
        step._observer = null;
      }

      // Render first batch
      this._renderAddonBatch(step, grid, perPage);

      // Show Load More button if there are more items
      if (step._loadMoreBtn) {
        step._loadMoreBtn.hidden = step._loadedCount >= step._displayProducts.length;
      }

      // Infinite scroll: single persistent observer.
      // When sentinel enters view, _fillVisible loads batches one-per-rAF
      // until the sentinel is pushed below the fold or everything is loaded.
      if (step._loadStyle !== 'button' && step._displayProducts.length > perPage) {
        step._observer = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this._fillVisible(step, grid, sentinel, perPage);
          },
          { rootMargin: '200px' }
        );
        step._observer.observe(sentinel);
      }
    }

    _fillVisible(step, grid, sentinel, perPage) {
      const total = (step._displayProducts || []).length;
      if (step._loadedCount >= total) return;
      this._renderAddonBatch(step, grid, perPage);
      if (step._loadedCount >= total) return;
      requestAnimationFrame(() => {
        const rect = sentinel.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 200) {
          this._fillVisible(step, grid, sentinel, perPage);
        }
      });
    }

    _renderAddonBatch(step, grid, perPage) {
      const products = step._displayProducts || [];
      const start = step._loadedCount;
      const end = Math.min(start + perPage, products.length);
      for (let i = start; i < end; i++) {
        grid.appendChild(this._buildProductCard(products[i], step));
      }
      step._loadedCount = end;

      // Done loading — disconnect observer and hide button
      if (step._loadedCount >= products.length) {
        if (step._observer) {
          step._observer.disconnect();
          step._observer = null;
        }
        if (step._loadMoreBtn) step._loadMoreBtn.hidden = true;
      }
    }

    _buildProductCard(product, step) {
      const availableVariants = (product.variants || []).filter((v) => v.available !== false);
      const defaultVariant = availableVariants[0] || product.variants?.[0];
      let selectedVariantId = String(defaultVariant?.id || '');

      const card = document.createElement('div');
      card.className = 'ma-quiz-wizard__addon-card';
      card.dataset.productId = product.id;

      // Image wrapper (for country/tag badges)
      const imgWrap = document.createElement('div');
      imgWrap.className = 'ma-quiz-wizard__addon-card-img-wrap';
      const imgEl = document.createElement('img');
      imgEl.className = 'ma-quiz-wizard__addon-card-img';
      imgEl.src = product.featured_image || '';
      imgEl.alt = product.title || '';
      imgEl.loading = 'lazy';
      if (!product.featured_image) imgEl.style.background = '#f3f4f6';
      imgEl.addEventListener('click', () => this._openProductModal(product, step));
      imgWrap.appendChild(imgEl);

      // Country badge
      const countryLabel = this._getCountry(product);
      if (countryLabel) {
        const countryBadge = document.createElement('span');
        countryBadge.className = 'ma-quiz-wizard__addon-country';
        countryBadge.setAttribute('aria-hidden', 'true');
        countryBadge.textContent = countryLabel;
        imgWrap.appendChild(countryBadge);
      }

      // Tag badge
      const tagBadge = this._getTagBadge(product.tags || []);
      if (tagBadge) {
        const tagEl = document.createElement('span');
        tagEl.className = 'ma-quiz-wizard__addon-tag' + (tagBadge.type === 'limited' ? ' ma-quiz-wizard__addon-tag--limited' : '');
        tagEl.setAttribute('aria-hidden', 'true');
        tagEl.textContent = tagBadge.label;
        imgWrap.appendChild(tagEl);
      }

      card.appendChild(imgWrap);

      // Body
      const body = document.createElement('div');
      body.className = 'ma-quiz-wizard__addon-card-body';

      // Title (clickable → modal)
      const titleEl = document.createElement('div');
      titleEl.className = 'ma-quiz-wizard__addon-card-title';
      titleEl.textContent = product.title;
      titleEl.addEventListener('click', () => this._openProductModal(product, step));
      body.appendChild(titleEl);

      // Price
      const priceEl = document.createElement('div');
      priceEl.className = 'ma-quiz-wizard__addon-card-price';
      priceEl.textContent = this._formatMoney(defaultVariant?.price || product.price || 0);
      body.appendChild(priceEl);

      // Controls row
      const controls = document.createElement('div');
      controls.className = 'ma-quiz-wizard__addon-card-controls';

      const hasMultipleVariants = availableVariants.length > 1;

      // Variant dropdown (only available variants)
      if (hasMultipleVariants) {
        const variantSelect = document.createElement('select');
        variantSelect.className = 'ma-quiz-wizard__addon-variant-select';
        variantSelect.setAttribute('aria-label', 'Select variant');
        availableVariants.forEach((v) => {
          const opt = document.createElement('option');
          opt.value = String(v.id);
          opt.textContent = v.title;
          variantSelect.appendChild(opt);
        });
        variantSelect.addEventListener('change', () => {
          const oldKey = `${product.id}-${selectedVariantId}`;
          const oldAddon = this._state.addons[oldKey];
          if (oldAddon) {
            const qty = oldAddon.quantity;
            delete this._state.addons[oldKey];
            selectedVariantId = variantSelect.value;
            this._setAddon(product, selectedVariantId, qty);
          } else {
            selectedVariantId = variantSelect.value;
          }
          const v = availableVariants.find((vv) => String(vv.id) === selectedVariantId);
          priceEl.textContent = this._formatMoney(v?.price || 0);
          updateCardState();
        });
        controls.appendChild(variantSelect);
      }

      // Qty stepper (hidden until item is added)
      const qtyWrap = document.createElement('div');
      qtyWrap.className = 'ma-quiz-wizard__addon-qty-wrap';
      qtyWrap.hidden = true;

      const btnMinus = document.createElement('button');
      btnMinus.type = 'button';
      btnMinus.className = 'ma-quiz-wizard__addon-qty-btn';
      btnMinus.textContent = '−';
      btnMinus.setAttribute('aria-label', 'Decrease quantity');
      qtyWrap.appendChild(btnMinus);

      const qtySpan = document.createElement('span');
      qtySpan.className = 'ma-quiz-wizard__addon-qty-num';
      qtySpan.textContent = '1';
      qtyWrap.appendChild(qtySpan);

      const btnPlus = document.createElement('button');
      btnPlus.type = 'button';
      btnPlus.className = 'ma-quiz-wizard__addon-qty-btn';
      btnPlus.textContent = '+';
      btnPlus.setAttribute('aria-label', 'Increase quantity');
      qtyWrap.appendChild(btnPlus);

      controls.appendChild(qtyWrap);

      // Add / Remove button
      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'ma-quiz-wizard__addon-btn';
      addBtn.textContent = '+ ' + (step.add_label || 'Add');
      controls.appendChild(addBtn);

      const updateCardState = () => {
        const key = `${product.id}-${selectedVariantId}`;
        const addon = this._state.addons[key];
        if (addon) {
          qtyWrap.hidden = false;
          qtyWrap.classList.add('is-added');
          qtySpan.textContent = String(addon.quantity);
          addBtn.hidden = true;
        } else {
          qtyWrap.hidden = true;
          qtyWrap.classList.remove('is-added');
          qtySpan.textContent = '1';
          addBtn.hidden = false;
          addBtn.textContent = '+ ' + (step.add_label || 'Add');
        }
      };

      btnMinus.addEventListener('click', () => {
        const key = `${product.id}-${selectedVariantId}`;
        const current = this._state.addons[key]?.quantity || 1;
        this._setAddon(product, selectedVariantId, current - 1);
        updateCardState();
        this._syncModalState(product, selectedVariantId);
      });

      btnPlus.addEventListener('click', () => {
        const key = `${product.id}-${selectedVariantId}`;
        const current = this._state.addons[key]?.quantity || 0;
        this._setAddon(product, selectedVariantId, current + 1);
        updateCardState();
        this._syncModalState(product, selectedVariantId);
      });

      addBtn.addEventListener('click', () => {
        const key = `${product.id}-${selectedVariantId}`;
        if (this._state.addons[key]) {
          this._setAddon(product, selectedVariantId, 0);
        } else {
          this._setAddon(product, selectedVariantId, 1);
        }
        updateCardState();
        this._syncModalState(product, selectedVariantId);
      });

      body.appendChild(controls);
      card.appendChild(body);

      // Set initial visual state
      updateCardState();

      return card;
    }

    // Update a card in the grid to reflect current addon state (called from modal interactions)
    _syncCardInGrid(product, variantId) {
      this._stepsContainer.querySelectorAll(`.ma-quiz-wizard__addon-card[data-product-id="${product.id}"]`).forEach((card) => {
        const variantSelect = card.querySelector('.ma-quiz-wizard__addon-variant-select');
        const currentVariantId = variantSelect ? variantSelect.value : variantId;
        const key = `${product.id}-${currentVariantId}`;
        const addon = this._state.addons[key];

        const qtyWrap = card.querySelector('.ma-quiz-wizard__addon-qty-wrap');
        const addBtn = card.querySelector('.ma-quiz-wizard__addon-btn');
        const qtySpan = card.querySelector('.ma-quiz-wizard__addon-qty-num');
        const grid = card.closest('.ma-quiz-wizard__addon-grid');
        const stepData = grid?.dataset.stepId ? this._stepMap[grid.dataset.stepId] : null;

        if (!qtyWrap || !addBtn || !qtySpan) return;

        if (addon) {
          qtyWrap.hidden = false;
          qtyWrap.classList.add('is-added');
          qtySpan.textContent = String(addon.quantity);
          addBtn.hidden = true;
        } else {
          qtyWrap.hidden = true;
          qtyWrap.classList.remove('is-added');
          qtySpan.textContent = '1';
          addBtn.hidden = false;
          addBtn.textContent = '+ ' + (stepData?.add_label || 'Add');
        }
      });
    }

    // ── Add-on state ──────────────────────────────────────────────

    _setAddon(product, variantId, qty) {
      const key = `${product.id}-${variantId}`;
      if (qty <= 0) {
        delete this._state.addons[key];
      } else {
        const variant = product.variants?.find((v) => String(v.id) === String(variantId));
        this._state.addons[key] = {
          product_id: product.id,
          variant_id: variantId,
          quantity: qty,
          title: `${product.title}${variant && variant.title !== 'Default Title' ? ' — ' + variant.title : ''}`,
        };
      }
    }

    // ── Product badge helpers ─────────────────────────────────

    _getCountry(product) {
      // Same mapping as ma-collection.js and main-product.liquid
      const NAME_TO_CODE = {
        'china': 'CN', 'japan': 'JP', 'south korea': 'KR', 'korea': 'KR',
        'taiwan': 'TW', 'thailand': 'TH', 'philippines': 'PH', 'vietnam': 'VN',
        'indonesia': 'ID', 'malaysia': 'MY', 'singapore': 'SG', 'india': 'IN',
        'united states': 'US', 'usa': 'US', 'uk': 'GB', 'united kingdom': 'GB',
        'mexico': 'MX', 'canada': 'CA', 'australia': 'AU', 'france': 'FR',
        'germany': 'DE', 'italy': 'IT', 'spain': 'ES', 'brazil': 'BR',
        'peru': 'PE', 'colombia': 'CO', 'argentina': 'AR', 'chile': 'CL',
        'hong kong': 'HK', 'turkey': 'TR', 'russia': 'RU', 'pakistan': 'PK',
      };
      const codeToEmoji = (code) => {
        if (!code || !/^[A-Za-z]{2}$/.test(code)) return null;
        return Array.from(code.toUpperCase())
          .map((c) => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65))
          .join('');
      };
      const nameToLabel = (name) => {
        const code = NAME_TO_CODE[name.trim().toLowerCase()];
        const flag = codeToEmoji(code);
        return flag ? `${flag} ${name.toUpperCase()}` : name.toUpperCase();
      };

      // Prefer metafield value (e.g. "Japan", "South Korea")
      if (product.country) return nameToLabel(product.country);
      // Fallback: product tags
      for (const tag of (product.tags || [])) {
        if (NAME_TO_CODE[tag.trim().toLowerCase()]) return nameToLabel(tag);
      }
      return null;
    }

    _getTagBadge(tags) {
      const lc = tags.map((t) => t.toLowerCase());
      if (lc.includes('limited')) return { label: 'LIMITED', type: 'limited' };
      if (lc.includes('staff-pick')) return { label: 'STAFF PICK', type: 'staff-pick' };
      if (lc.includes('new-arrival')) return { label: 'NEW', type: 'new' };
      return null;
    }

    // ── Product detail modal ──────────────────────────────────────

    _buildModalContainer() {
      const modal = document.createElement('div');
      modal.className = 'ma-quiz-wizard__modal';
      modal.hidden = true;
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');

      // Overlay — click to close
      const overlay = document.createElement('div');
      overlay.className = 'ma-quiz-wizard__modal-overlay';
      overlay.addEventListener('click', () => this._closeProductModal());
      modal.appendChild(overlay);

      // Content: 2-col on desktop (image | panel), stacked on mobile
      const content = document.createElement('div');
      content.className = 'ma-quiz-wizard__modal-content';

      // Close button — absolute inside content; appears over image on mobile,
      // inside right panel on desktop due to 2-col layout
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'ma-quiz-wizard__modal-close';
      closeBtn.textContent = '✕';
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.addEventListener('click', () => this._closeProductModal());
      content.appendChild(closeBtn);

      // ── Image side (carousel) ──
      const carousel = document.createElement('div');
      carousel.className = 'ma-quiz-wizard__modal-carousel';

      // Country badge slot — populated in _openProductModal
      const countryBadge = document.createElement('span');
      countryBadge.className = 'ma-quiz-wizard__modal-country';
      countryBadge.hidden = true;
      carousel.appendChild(countryBadge);

      const slides = document.createElement('div');
      slides.className = 'ma-quiz-wizard__modal-slides';
      carousel.appendChild(slides);

      const dotsContainer = document.createElement('div');
      dotsContainer.className = 'ma-quiz-wizard__modal-dots';
      carousel.appendChild(dotsContainer);

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'ma-quiz-wizard__modal-prev';
      prevBtn.textContent = '‹';
      prevBtn.setAttribute('aria-label', 'Previous image');
      carousel.appendChild(prevBtn);

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'ma-quiz-wizard__modal-next';
      nextBtn.textContent = '›';
      nextBtn.setAttribute('aria-label', 'Next image');
      carousel.appendChild(nextBtn);

      content.appendChild(carousel);

      // ── Panel side (body + footer) ──
      const panel = document.createElement('div');
      panel.className = 'ma-quiz-wizard__modal-panel';

      // Body — rebuilt each open via _openProductModal
      const body = document.createElement('div');
      body.className = 'ma-quiz-wizard__modal-body';
      panel.appendChild(body);

      // Footer — price + qty + add button; rebuilt each open
      const footer = document.createElement('div');
      footer.className = 'ma-quiz-wizard__modal-footer';
      panel.appendChild(footer);

      content.appendChild(panel);
      modal.appendChild(content);

      return modal;
    }

    _openProductModal(product, step) {
      const modal = this._modal;
      modal._productId = product.id;

      // ── Carousel ──
      const slides = modal.querySelector('.ma-quiz-wizard__modal-slides');
      const dotsContainer = modal.querySelector('.ma-quiz-wizard__modal-dots');
      slides.innerHTML = '';
      dotsContainer.innerHTML = '';

      const images = product.images?.length ? product.images : (product.featured_image ? [product.featured_image] : []);
      let currentSlide = 0;

      images.forEach((src, i) => {
        const slide = document.createElement('div');
        slide.className = 'ma-quiz-wizard__modal-slide';
        const img = document.createElement('img');
        img.src = src;
        img.alt = product.title || '';
        img.loading = i === 0 ? 'eager' : 'lazy';
        slide.appendChild(img);
        slides.appendChild(slide);

        if (images.length > 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'ma-quiz-wizard__modal-dot' + (i === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', `Image ${i + 1}`);
          dot.addEventListener('click', () => {
            const slideEls = slides.querySelectorAll('.ma-quiz-wizard__modal-slide');
            const target = slideEls[i];
            if (target) slides.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
          });
          dotsContainer.appendChild(dot);
        }
      });

      const multiImage = images.length > 1;

      // Peek mode: show ~15% of next slide when multiple images exist
      slides.classList.toggle('ma-quiz-wizard__modal-slides--peek', multiImage);

      const allSlides = () => Array.from(slides.querySelectorAll('.ma-quiz-wizard__modal-slide'));
      const scrollToSlide = (idx) => {
        const el = allSlides()[idx];
        if (el) slides.scrollTo({ left: el.offsetLeft, behavior: 'smooth' });
      };

      // Replace prev/next buttons to clear old listeners
      const oldPrev = modal.querySelector('.ma-quiz-wizard__modal-prev');
      const oldNext = modal.querySelector('.ma-quiz-wizard__modal-next');
      const newPrev = oldPrev.cloneNode(true);
      const newNext = oldNext.cloneNode(true);
      oldPrev.parentNode.replaceChild(newPrev, oldPrev);
      oldNext.parentNode.replaceChild(newNext, oldNext);

      newPrev.hidden = !multiImage;
      newNext.hidden = !multiImage;
      dotsContainer.hidden = !multiImage;

      newPrev.addEventListener('click', () => {
        currentSlide = Math.max(0, currentSlide - 1);
        scrollToSlide(currentSlide);
      });
      newNext.addEventListener('click', () => {
        currentSlide = Math.min(images.length - 1, currentSlide + 1);
        scrollToSlide(currentSlide);
      });

      // Update dots on scroll (native swipe or button)
      if (modal._scrollListener) slides.removeEventListener('scroll', modal._scrollListener);
      let scrollDebounce;
      modal._scrollListener = () => {
        clearTimeout(scrollDebounce);
        scrollDebounce = setTimeout(() => {
          const slideEls = allSlides();
          if (!slideEls.length) return;
          // Find which slide is most visible by checking offsetLeft proximity to scrollLeft
          let closest = 0;
          let minDist = Infinity;
          slideEls.forEach((el, i) => {
            const dist = Math.abs(el.offsetLeft - slides.scrollLeft);
            if (dist < minDist) { minDist = dist; closest = i; }
          });
          currentSlide = closest;
          dotsContainer.querySelectorAll('.ma-quiz-wizard__modal-dot').forEach((d, i) => {
            d.classList.toggle('is-active', i === currentSlide);
          });
        }, 50);
      };
      slides.addEventListener('scroll', modal._scrollListener);

      // ── Country badge in image area ──
      const countryBadge = modal.querySelector('.ma-quiz-wizard__modal-country');
      if (countryBadge) {
        const countryLabel = this._getCountry(product);
        countryBadge.textContent = countryLabel || '';
        countryBadge.hidden = !countryLabel;
      }

      // ── Shared state (declared early so closures can reference them) ──
      const availableVariants = (product.variants || []).filter((v) => v.available !== false);
      const hasMultiple = availableVariants.length > 1;
      let selectedVariantId = String(availableVariants[0]?.id || product.variants?.[0]?.id || '');

      // ── Footer elements (created first so updatePrice can close over priceEl) ──
      const priceEl = document.createElement('div');
      priceEl.className = 'ma-quiz-wizard__modal-price';

      const qtyWrap = document.createElement('div');
      qtyWrap.className = 'ma-quiz-wizard__modal-qty-wrap';
      qtyWrap.hidden = true;

      const minusBtn = document.createElement('button');
      minusBtn.type = 'button';
      minusBtn.className = 'ma-quiz-wizard__addon-qty-btn';
      minusBtn.textContent = '−';
      minusBtn.setAttribute('aria-label', 'Decrease quantity');
      qtyWrap.appendChild(minusBtn);

      const qtyNum = document.createElement('span');
      qtyNum.className = 'ma-quiz-wizard__modal-qty-num';
      qtyNum.textContent = '1';
      qtyWrap.appendChild(qtyNum);

      const plusBtn = document.createElement('button');
      plusBtn.type = 'button';
      plusBtn.className = 'ma-quiz-wizard__addon-qty-btn';
      plusBtn.textContent = '+';
      plusBtn.setAttribute('aria-label', 'Increase quantity');
      qtyWrap.appendChild(plusBtn);

      const actionBtn = document.createElement('button');
      actionBtn.type = 'button';
      actionBtn.className = 'ma-quiz-wizard__modal-btn';

      const updatePrice = () => {
        const v = product.variants?.find((vv) => String(vv.id) === selectedVariantId);
        priceEl.textContent = this._formatMoney(v?.price || product.price || 0);
      };

      const refreshState = () => {
        const key = `${product.id}-${selectedVariantId}`;
        const addon = this._state.addons[key];
        if (addon) {
          qtyWrap.hidden = false;
          qtyNum.textContent = String(addon.quantity);
          actionBtn.textContent = step.remove_label || 'Remove';
          actionBtn.classList.add('is-added');
        } else {
          qtyWrap.hidden = true;
          qtyNum.textContent = '1';
          actionBtn.textContent = '+ Add to box';
          actionBtn.classList.remove('is-added');
        }
      };

      // ── Build body: kicker → title → description → [variant select] ──
      const body = modal.querySelector('.ma-quiz-wizard__modal-body');
      body.innerHTML = '';

      const kickerParts = [];
      if (product.country) kickerParts.push(product.country.toUpperCase());
      const tagBadgeInfo = this._getTagBadge(product.tags || []);
      if (tagBadgeInfo) kickerParts.push(tagBadgeInfo.label);
      if (kickerParts.length) {
        const kickerEl = document.createElement('div');
        kickerEl.className = 'ma-quiz-wizard__modal-kicker';
        kickerEl.textContent = kickerParts.join(' · ');
        body.appendChild(kickerEl);
      }

      const titleEl = document.createElement('h3');
      titleEl.className = 'ma-quiz-wizard__modal-title';
      titleEl.textContent = product.title || '';
      body.appendChild(titleEl);

      const descEl = document.createElement('div');
      descEl.className = 'ma-quiz-wizard__modal-description';
      descEl.innerHTML = product.description || '';
      body.appendChild(descEl);

      if (hasMultiple) {
        const variantSelect = document.createElement('select');
        variantSelect.className = 'ma-quiz-wizard__modal-variant-select';
        availableVariants.forEach((v) => {
          const opt = document.createElement('option');
          opt.value = String(v.id);
          opt.textContent = v.title;
          variantSelect.appendChild(opt);
        });
        variantSelect.value = selectedVariantId;
        variantSelect.addEventListener('change', () => {
          const oldKey = `${product.id}-${selectedVariantId}`;
          const oldAddon = this._state.addons[oldKey];
          if (oldAddon) {
            const qty = oldAddon.quantity;
            delete this._state.addons[oldKey];
            selectedVariantId = variantSelect.value;
            this._setAddon(product, selectedVariantId, qty);
          } else {
            selectedVariantId = variantSelect.value;
          }
          updatePrice();
          refreshState();
          this._syncCardInGrid(product, selectedVariantId);
        });
        body.appendChild(variantSelect);
      }

      // ── Build footer: price → [qty stepper + add button] ──
      const footer = modal.querySelector('.ma-quiz-wizard__modal-footer');
      footer.innerHTML = '';

      footer.appendChild(priceEl);
      updatePrice();

      const actionRow = document.createElement('div');
      actionRow.className = 'ma-quiz-wizard__modal-action-row';
      actionRow.appendChild(qtyWrap);
      actionRow.appendChild(actionBtn);
      footer.appendChild(actionRow);

      refreshState();

      minusBtn.addEventListener('click', () => {
        const key = `${product.id}-${selectedVariantId}`;
        const current = this._state.addons[key]?.quantity || 1;
        this._setAddon(product, selectedVariantId, current - 1);
        refreshState();
        this._syncCardInGrid(product, selectedVariantId);
      });

      plusBtn.addEventListener('click', () => {
        const key = `${product.id}-${selectedVariantId}`;
        const current = this._state.addons[key]?.quantity || 0;
        this._setAddon(product, selectedVariantId, current + 1);
        refreshState();
        this._syncCardInGrid(product, selectedVariantId);
      });

      actionBtn.addEventListener('click', () => {
        const key = `${product.id}-${selectedVariantId}`;
        if (this._state.addons[key]) {
          this._setAddon(product, selectedVariantId, 0);
        } else {
          this._setAddon(product, selectedVariantId, 1);
        }
        refreshState();
        this._syncCardInGrid(product, selectedVariantId);
      });

      // ESC to close
      if (modal._keyListener) document.removeEventListener('keydown', modal._keyListener);
      modal._keyListener = (e) => {
        if (e.key === 'Escape') this._closeProductModal();
      };
      document.addEventListener('keydown', modal._keyListener);

      modal.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    // Sync the modal's qty/state when a card interaction updates the same product
    _syncModalState(product, variantId) {
      const modal = this._modal;
      if (!modal || modal.hidden || modal._productId !== product.id) return;
      const key = `${product.id}-${variantId}`;
      const addon = this._state.addons[key];
      const qtyWrap = modal.querySelector('.ma-quiz-wizard__modal-qty-wrap');
      const qtyNum = modal.querySelector('.ma-quiz-wizard__modal-qty-num');
      const actionBtn = modal.querySelector('.ma-quiz-wizard__modal-btn');
      if (!qtyWrap || !actionBtn) return;
      // Try to get step labels from _stepMap
      const currentStepId = this._state.currentStepId;
      const stepData = this._stepMap[currentStepId];
      if (addon) {
        qtyWrap.hidden = false;
        if (qtyNum) qtyNum.textContent = String(addon.quantity);
        actionBtn.textContent = stepData?.remove_label || 'Remove';
        actionBtn.classList.add('is-added');
      } else {
        qtyWrap.hidden = true;
        if (qtyNum) qtyNum.textContent = '1';
        actionBtn.textContent = stepData?.add_label || 'Add';
        actionBtn.classList.remove('is-added');
      }
    }

    _closeProductModal() {
      if (!this._modal) return;
      this._modal.hidden = true;
      this._modal._productId = null;
      document.body.style.overflow = '';
      if (this._modal._keyListener) {
        document.removeEventListener('keydown', this._modal._keyListener);
        this._modal._keyListener = null;
      }
    }

    // ── Sticky bottom bar ─────────────────────────────────────────

    _buildStickyBar() {
      const bar = document.createElement('div');
      bar.className = 'ma-quiz-wizard__sticky-bar';

      const inner = document.createElement('div');
      inner.className = 'ma-quiz-wizard__sticky-bar-inner page-width';

      const msg = document.createElement('div');
      msg.className = 'ma-quiz-wizard__sticky-bar-message';
      this._stickyBarMsg = msg;
      inner.appendChild(msg);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ma-quiz-wizard__sticky-bar-btn';
      btn.textContent = 'Continue';
      btn.addEventListener('click', () => {
        if (this._btnFinish && !this._btnFinish.hidden) {
          this._finish();
        } else {
          this._goNext();
        }
      });

      this._stickyBarBtn = btn;
      inner.appendChild(btn);
      bar.appendChild(inner);
      return bar;
    }

    _initStickyScroll() {
      let lastScrollY = window.scrollY;
      let ticking = false;

      this._scrollHandler = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const diff = currentScrollY - lastScrollY;
          const step = this._stepMap?.[this._state?.currentStepId];

          // Only active on collection steps; bail out on any other condition
          if (!step || step.step_type !== 'collection' || this._state.completed || this._state.adding) {
            this._hideStickyBar();
            lastScrollY = currentScrollY;
            ticking = false;
            return;
          }

          // Hide when the real nav buttons are actually visible on screen
          const nav = this.querySelector('.ma-quiz-wizard__nav');
          const navRect = nav?.getBoundingClientRect();
          const navOnScreen = navRect && navRect.top < window.innerHeight && navRect.bottom > 0;

          if (navOnScreen) {
            this._hideStickyBar();
          } else if (diff > 5) {
            // Scrolling down + nav off screen → slide up into view
            this._showStickyBar();
          } else if (diff < -5) {
            // Scrolling up → slide back down
            this._hideStickyBar();
          }

          lastScrollY = currentScrollY;
          ticking = false;
        });
      };

      window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }

    _showStickyBar() {
      this._stickyBar?.classList.add('is-visible');
    }

    _hideStickyBar() {
      this._stickyBar?.classList.remove('is-visible');
    }

    _updateStickyBar() {
      if (!this._stickyBarBtn) return;
      const currentId = this._state.currentStepId;
      const step = this._stepMap[currentId];
      if (step?.step_type !== 'collection') {
        this._hideStickyBar();
        return;
      }

      // Update per-step message
      if (this._stickyBarMsg) {
        this._stickyBarMsg.innerHTML = step.sticky_bar_message || '';
      }

      const isLast = this._isLastStep(currentId);
      // Mirror label from the real button so they always match
      this._stickyBarBtn.textContent = isLast
        ? (this._btnFinish?.textContent?.trim() || this._finishLabel || 'Add to Cart')
        : (this._btnNext?.textContent?.trim() || this._nextLabel || 'Continue');
      this._stickyBarBtn.disabled = this._state.adding;
    }

    // ── Success screen ────────────────────────────────────────────

    _showSuccessScreen() {
      if (!this._successPanel) return;
      this._stepsContainer.hidden = true;
      this._successPanel.hidden = false;
      this._hideStickyBar();

      if (this._btnBack) this._btnBack.hidden = true;
      if (this._btnNext) this._btnNext.hidden = true;
      if (this._btnFinish) this._btnFinish.hidden = true;
      if (this._btnSkip) this._btnSkip.hidden = true;
      if (this._selectionCountEl) this._selectionCountEl.hidden = true;

      // Complete the progress bar
      if (this._progressFill) this._progressFill.style.width = '100%';

      const heading = this._successPanel.querySelector('.ma-quiz-wizard__success-title');
      if (heading) requestAnimationFrame(() => heading.focus());
    }

    _resetQuiz() {
      // Full reset — start over from step 1 with no selections
      this._state.selections = {};
      this._state.addons = {};
      this._state.giftMessages = {};
      this._state.completed = false;
      this._state.adding = false;
      this._state.history = [];
      this._state.currentStepId = this._steps[0].step_id;

      try { localStorage.removeItem(this._storageKey); } catch (_) {}

      // Clean up per-step resources so _renderSteps doesn't leak DOM nodes / observers
      Object.values(this._stepMap).forEach((s) => {
        if (s._toolbarIO) { s._toolbarIO.disconnect(); s._toolbarIO = null; }
        if (s._stickyToolbar) { s._stickyToolbar.remove(); s._stickyToolbar = null; }
        if (s._observer) { s._observer.disconnect(); s._observer = null; }
      });

      this._closeProductModal();
      this._hideStickyBar();

      if (this._successPanel) this._successPanel.hidden = true;
      if (this._stepsContainer) this._stepsContainer.hidden = false;

      this._renderSteps();
      this._updateProgress();
      this._updateNav();
      this._hideError();
    }

    // ── Navigation ────────────────────────────────────────────────

    _bindNav() {
      this._btnBack?.addEventListener('click', () => this._goBack());
      this._btnNext?.addEventListener('click', () => this._goNext());
      this._btnFinish?.addEventListener('click', () => this._finish());
      this.querySelector('.ma-quiz-wizard__btn-restart')?.addEventListener('click', () => this._resetQuiz());
      this._btnStartOver?.addEventListener('click', () => this._resetQuiz());
      if (this._headerBackBtn) {
        this._headerBackBtn.addEventListener('click', () => this._goBack());
        // Mirror label from the wizard's own back button
        if (this._btnBack) this._headerBackBtn.textContent = this._btnBack.textContent.trim();
      }
    }

    _goToStep(stepId) {
      this._state.history.push(this._state.currentStepId);
      this._state.currentStepId = stepId;
      this._activateStep(stepId, true);
      this._updateProgress();
      this._updateNav();
      this._hideError();
    }

    _goBack() {
      if (!this._state.history.length) return;
      const prevId = this._state.history.pop();
      this._state.currentStepId = prevId;
      this._activateStep(prevId, true);
      this._updateProgress();
      this._updateNav();
      this._hideError();
    }

    _goNext() {
      if (!this._canProceed(this._state.currentStepId)) return;
      this._saveRememberedSelections();
      const nextId = this._getNextStep(this._state.currentStepId);
      if (nextId) this._goToStep(nextId);
    }

    _skip() {
      const step = this._stepMap[this._state.currentStepId];
      if (step?.skip_mode === 'to_required') {
        this._skipToRequired();
      } else {
        this._skipOne();
      }
    }

    _skipOne() {
      const currentId = this._state.currentStepId;
      this._state.selections[currentId] = ['__skipped__'];
      this._updateTileStates(currentId);
      this._saveRememberedSelections();
      const nextId = this._getNextStep(currentId);
      if (nextId) this._goToStep(nextId);
    }

    _skipToRequired() {
      const currentIdx = this._steps.findIndex((s) => s.step_id === this._state.currentStepId);
      let targetIdx = -1;
      for (let i = currentIdx + 1; i < this._steps.length; i++) {
        if (!this._steps[i].skippable) { targetIdx = i; break; }
      }
      if (targetIdx === -1) return;

      // Mark every skipped step with a sentinel so the skip is remembered in localStorage
      for (let i = currentIdx; i < targetIdx; i++) {
        const sid = this._steps[i].step_id;
        this._state.selections[sid] = ['__skipped__'];
        this._updateTileStates(sid);
      }

      this._saveRememberedSelections();

      // Push all skipped steps into history so Back works step-by-step
      for (let i = currentIdx; i < targetIdx; i++) {
        this._state.history.push(this._steps[i].step_id);
      }

      const targetId = this._steps[targetIdx].step_id;
      this._state.currentStepId = targetId;
      this._activateStep(targetId);
      this._updateNav();
    }

    _getNextStep(stepId) {
      const selected = this._state.selections[stepId] || [];
      const answers = this._answerMap[stepId] || [];

      for (const val of selected) {
        const answer = answers.find((a) => a.value === val);
        if (answer?.next_step_override) return answer.next_step_override;
      }

      const step = this._stepMap[stepId];
      if (step?.next_step_default) return step.next_step_default;

      const idx = this._steps.findIndex((s) => s.step_id === stepId);
      if (idx >= 0 && idx < this._steps.length - 1) {
        const nextId = this._steps[idx + 1].step_id;
        if (nextId === 'product_gift_choice' && !this._hasPlanGift2OptIn('product_gift_choice')) {
          return this._getNextStep(nextId);
        }
        return nextId;
      }

      return null;
    }

    _hasPlanGift2OptIn(optInStepId) {
      for (const answers of Object.values(this._answerMap)) {
        for (const answer of answers) {
          const sel = this._state.selections[answer.step_id] || [];
          if (sel.includes(answer.value) && answer.free_gift_2_opt_in_step_id === optInStepId && answer.free_gift_2_variant_id) {
            return true;
          }
        }
      }
      return false;
    }

    _canProceed(stepId) {
      const step = this._stepMap[stepId];
      if (step?.step_type === 'collection') return true;
      if (step?.step_type === 'message') {
        const vals = this._state.giftMessages[stepId] || {};
        if (step.recipient_required && !(vals.recipient || '').trim()) return false;
        if (step.message_required && !(vals.message || '').trim()) return false;
        return true;
      }
      if (step?.addon_mode) return true;
      const sel = (this._state.selections[stepId] || []).filter(v => v !== '__skipped__');
      return sel.length > 0;
    }

    _isLastStep(stepId) {
      return this._getNextStep(stepId) === null;
    }

    // ── Tile interaction ──────────────────────────────────────────

    _onTileClick(stepId, value) {
      const step = this._stepMap[stepId];
      const inputType = step?.input_type || 'radio';

      if (inputType === 'checkbox') {
        const current = (this._state.selections[stepId] || []).filter(v => v !== '__skipped__');
        const idx = current.indexOf(value);
        if (idx >= 0) current.splice(idx, 1);
        else current.push(value);
        this._state.selections[stepId] = current;
      } else {
        this._state.selections[stepId] = [value];
      }

      this._updateTileStates(stepId);
      this._updateNav();

      if (step?.auto_advance && inputType === 'radio') {
        setTimeout(() => {
          if (this._isLastStep(stepId)) this._finish();
          else this._goNext();
        }, 350);
      }

      document.dispatchEvent(
        new CustomEvent('maQuizAnswer', {
          bubbles: true,
          detail: { stepId, value, allSelections: Object.assign({}, this._state.selections) },
        }),
      );
    }

    _updateTileStates(stepId) {
      const selected = this._state.selections[stepId] || [];
      const panel = this._stepsContainer.querySelector(`.ma-quiz-wizard__step[data-step-id="${stepId}"]`);
      if (!panel) return;
      panel.querySelectorAll('.ma-quiz-wizard__tile').forEach((tile) => {
        const isSelected = selected.includes(tile.dataset.value);
        tile.classList.toggle('is-selected', isSelected);
        tile.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        // Update select pill text on image tiles
        const pill = tile.querySelector('.ma-quiz-wizard__tile-select-pill');
        if (pill) pill.textContent = isSelected ? '✓ Selected' : 'Choose this box';
        // Update addon button text
        const addonBtn = tile.querySelector('.ma-quiz-wizard__tile-addon-btn');
        if (addonBtn) addonBtn.textContent = isSelected ? '✓ Upgraded' : 'Upgrade Box';
      });
    }

    // ── Progress ──────────────────────────────────────────────────

    _updateProgress() {
      const total = this._steps.length;
      const currentIdx = this._steps.findIndex((s) => s.step_id === this._state.currentStepId);
      // Never reaches 100 during the quiz — 100% is reserved for the success screen
      const pct = total > 1 ? Math.round((currentIdx / total) * 100) : 0;

      if (this._progressFill) this._progressFill.style.width = `${pct}%`;
    }

    // ── Nav button state ──────────────────────────────────────────

    _updateNav() {
      const currentId = this._state.currentStepId;
      const step = this._stepMap[currentId];
      const isLast = this._isLastStep(currentId);
      const canGo = this._canProceed(currentId);
      const hasBack = this._state.history.length > 0;
      if (this._btnBack) this._btnBack.hidden = !hasBack;
      if (this._headerBackBtn) this._headerBackBtn.hidden = !hasBack;
      if (this._btnNext) this._btnNext.hidden = isLast;
      if (this._btnFinish) this._btnFinish.hidden = !isLast;

      // Signal auto-advance step for CSS (hides CTA button on mobile)
      this.classList.toggle('has-auto-advance-step', !!step?.auto_advance);

      if (this._btnNext) this._btnNext.disabled = !canGo;
      if (this._btnFinish) this._btnFinish.disabled = !canGo || this._state.adding;

      const isFirstStep = currentId === this._steps[0].step_id;
      if (this._btnStartOver) this._btnStartOver.hidden = isFirstStep;

      // Selection count for checkbox steps
      if (this._selectionCountEl) {
        const sel = (this._state.selections[currentId] || []).filter((v) => v !== '__skipped__');
        const showCount = step?.input_type === 'checkbox' && sel.length > 0;
        this._selectionCountEl.hidden = !showCount;
        if (showCount) this._selectionCountEl.textContent = `${sel.length} SELECTED`;
      }

      // Dynamic CTA text: "Continue · Label" or "Add to Cart · Label" (radio steps only)
      const sel = (this._state.selections[currentId] || []).filter((v) => v !== '__skipped__');
      if (sel.length > 0 && step?.input_type === 'radio' && step?.step_type !== 'collection' && step?.step_type !== 'message') {
        const answers = this._answerMap[currentId] || [];
        const selAnswer = answers.find((a) => a.value === sel[0]);
        const label = selAnswer?.label || selAnswer?.value || null;
        if (label) {
          if (this._btnNext && !isLast) this._btnNext.textContent = `Continue · ${label}`;
          if (this._btnFinish && isLast) this._btnFinish.textContent = `${this._finishLabel} · ${label}`;
        } else {
          if (this._btnNext) this._btnNext.textContent = this._nextLabel;
          if (this._btnFinish) this._btnFinish.textContent = this._finishLabel;
        }
      } else {
        if (this._btnNext) this._btnNext.textContent = this._nextLabel;
        if (this._btnFinish) this._btnFinish.textContent = this._finishLabel;
      }

      this._updateStickyBar();
    }

    // ── Dynamic variant resolution ────────────────────────────────

    _resolveVariantFromSelections() {
      const optionValues = [];

      // Collect variant_option_value from all selected answers
      for (const [stepId, selected] of Object.entries(this._state.selections)) {
        const answers = this._answerMap[stepId] || [];
        for (const val of selected) {
          const answer = answers.find((a) => a.value === val);
          if (answer?.variant_option_value) optionValues.push(answer.variant_option_value);
        }
      }

      // Collect default_variant_option_value for ALL checkbox steps, including
      // addon steps the user skipped past without making any selection
      for (const step of this._steps) {
        if (step.input_type !== 'checkbox') continue;
        const selected = this._state.selections[step.step_id] || [];
        const answers = this._answerMap[step.step_id] || [];
        for (const answer of answers) {
          if (!selected.includes(answer.value) && answer.default_variant_option_value) {
            optionValues.push(answer.default_variant_option_value);
          }
        }
      }

      if (!optionValues.length) return null;
      const matches = this._variants.filter((v) =>
        optionValues.every((ov) => v.options.includes(ov))
      );
      return matches.length === 1 ? String(matches[0].id) : null;
    }

    _getActivePlanAnswer() {
      for (const [stepId, selected] of Object.entries(this._state.selections)) {
        const answers = this._answerMap[stepId] || [];
        for (const val of selected) {
          const answer = answers.find((a) => a.value === val && a.answer_type === 'plan');
          if (answer) return answer;
        }
      }
      return null;
    }

    _getEligibleGifts(answer, entry, forDisplay = false) {
      const billedCents = entry ? entry.price : 0;
      const gifts = [];

      if (this._isPlanGiftEligible(answer, forDisplay) && answer.free_gift_variant_id) {
        gifts.push({
          variant_id: answer.free_gift_variant_id,
          label: answer.free_gift_label ? answer.free_gift_label.split('\n')[0] : 'Free Gift',
          detail: answer.free_gift_detail || '',
          image_url: answer.free_gift_image_url || '',
          selling_plan_id: answer.free_gift_selling_plan_id || null,
        });
      }

      if (this._isPlanGift2Eligible(answer, forDisplay) && answer.free_gift_2_variant_id) {
        gifts.push({
          variant_id: answer.free_gift_2_variant_id,
          label: answer.free_gift_2_label ? answer.free_gift_2_label.split('\n')[0] : 'Free Gift',
          detail: answer.free_gift_2_detail || '',
          image_url: answer.free_gift_2_image_url || '',
          selling_plan_id: answer.free_gift_2_selling_plan_id || null,
        });
      }

      for (const cg of (this._cartGifts || [])) {
        if (billedCents >= cg.threshold) {
          gifts.push({
            variant_id: String(cg.variant_id),
            label: cg.label || 'Bonus Gift',
            detail: cg.detail || '',
            image_url: cg.image_url || '',
            selling_plan_id: null,
          });
        }
      }

      return gifts;
    }

    _openGiftOverlay(planName, gifts) {
      document.querySelector('.ma-quiz-wizard__gift-overlay')?.remove();

      const isDesktop = window.matchMedia('(min-width: 750px)').matches;

      const overlay = document.createElement('div');
      overlay.className = 'ma-quiz-wizard__gift-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', `Free gifts included with ${planName}`);

      const sheet = document.createElement('div');
      sheet.className = 'ma-quiz-wizard__gift-sheet';
      sheet.addEventListener('click', (e) => e.stopPropagation());

      const close = () => overlay.remove();
      overlay.addEventListener('click', close);

      const onKeyDown = (e) => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKeyDown); } };
      document.addEventListener('keydown', onKeyDown);
      overlay.addEventListener('click', () => document.removeEventListener('keydown', onKeyDown));

      if (!isDesktop) {
        const handle = document.createElement('div');
        handle.className = 'ma-quiz-wizard__gift-sheet-handle';
        sheet.appendChild(handle);
      }

      const header = document.createElement('div');
      header.className = 'ma-quiz-wizard__gift-sheet-header';

      const headerText = document.createElement('div');
      const overline = document.createElement('div');
      overline.className = 'ma-quiz-wizard__gift-sheet-overline';
      overline.textContent = `🎁 Free with ${planName}`;
      const title = document.createElement('div');
      title.className = 'ma-quiz-wizard__gift-sheet-title';
      title.textContent = `${gifts.length} Free Gift${gifts.length > 1 ? 's' : ''} Included`;
      headerText.appendChild(overline);
      headerText.appendChild(title);

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'ma-quiz-wizard__gift-sheet-close';
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.textContent = '✕';
      closeBtn.addEventListener('click', close);

      header.appendChild(headerText);
      header.appendChild(closeBtn);
      sheet.appendChild(header);

      const list = document.createElement('div');
      list.className = 'ma-quiz-wizard__gift-list';
      gifts.forEach((g) => {
        const item = document.createElement('div');
        item.className = 'ma-quiz-wizard__gift-item';

        const thumb = document.createElement('div');
        thumb.className = 'ma-quiz-wizard__gift-item-thumb';
        if (g.image_url) {
          const img = document.createElement('img');
          img.src = g.image_url;
          img.alt = '';
          thumb.appendChild(img);
        } else {
          thumb.textContent = '🎁';
        }

        const info = document.createElement('div');
        const nameEl = document.createElement('div');
        nameEl.className = 'ma-quiz-wizard__gift-item-name';
        nameEl.textContent = g.label;
        info.appendChild(nameEl);
        if (g.detail) {
          const detailEl = document.createElement('div');
          detailEl.className = 'ma-quiz-wizard__gift-item-detail';
          detailEl.textContent = g.detail;
          info.appendChild(detailEl);
        }

        item.appendChild(thumb);
        item.appendChild(info);
        list.appendChild(item);
      });
      sheet.appendChild(list);

      const gotIt = document.createElement('button');
      gotIt.type = 'button';
      gotIt.className = 'ma-quiz-wizard__gift-got-it';
      gotIt.textContent = 'Got it';
      gotIt.addEventListener('click', close);
      sheet.appendChild(gotIt);

      overlay.appendChild(sheet);
      document.body.appendChild(overlay);

      closeBtn.focus();
    }

    _isPlanGiftEligible(answer, forDisplay = false) {
      if (!answer.free_gift_variant_id) return false;
      if (!forDisplay && answer.free_gift_opt_in_step_id) {
        const optInSelections = this._state.selections[answer.free_gift_opt_in_step_id] || [];
        if (!optInSelections.includes(answer.free_gift_opt_in_value)) return false;
      }
      if (!answer.free_gift_eligible_values) return true;
      const eligible = answer.free_gift_eligible_values.split(',').map((v) => v.trim()).filter(Boolean);
      if (!eligible.length) return true;
      for (const [stepId, selected] of Object.entries(this._state.selections)) {
        const stepAnswers = this._answerMap[stepId] || [];
        for (const val of selected) {
          const a = stepAnswers.find((sa) => sa.value === val);
          if (a?.variant_option_value && eligible.includes(a.variant_option_value)) return true;
        }
      }
      return false;
    }

    _isPlanGift2Eligible(answer, forDisplay = false) {
      console.log('[Gift2]', answer.value, 'variant:', answer.free_gift_2_variant_id, 'forDisplay:', forDisplay);
      if (!answer.free_gift_2_variant_id) return false;
      if (!forDisplay && answer.free_gift_2_opt_in_step_id) {
        const optInSelections = this._state.selections[answer.free_gift_2_opt_in_step_id] || [];
        if (!optInSelections.includes(answer.free_gift_2_opt_in_value)) return false;
      }
      return true;
    }

    // ── Rule engine ───────────────────────────────────────────────

    _evaluateResults() {
      let bestMatch = null;
      let bestScore = -1;
      let defaultResult = null;

      for (const result of this._results) {
        const conditionsRaw = (result.conditions || '').trim();

        if (!conditionsRaw) {
          if (!defaultResult) defaultResult = result;
          continue;
        }

        const conditions = conditionsRaw
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean);
        const allMatch = conditions.every((cond) => {
          const [stepId, value] = cond.split('=');
          return (this._state.selections[stepId?.trim()] || []).includes(value?.trim());
        });

        if (allMatch) {
          const priority = parseInt(result.priority, 10) || 0;
          if (priority > bestScore) {
            bestScore = priority;
            bestMatch = result;
          }
        }
      }

      return bestMatch || defaultResult || null;
    }

    // ── Finish ────────────────────────────────────────────────────

    _finish() {
      if (this._state.adding) return;
      this._saveRememberedSelections();

      const dynamicVariantId = this._resolveVariantFromSelections();
      if (dynamicVariantId) {
        const planAnswer = this._getActivePlanAnswer();
        const planEntry = planAnswer?.plan_name_match
          ? this._getAllocationByName(dynamicVariantId, planAnswer.plan_name_match)
          : null;

        const subscriptionItem = { id: parseInt(dynamicVariantId, 10), quantity: 1 };
        if (planEntry) subscriptionItem.selling_plan = parseInt(planEntry.plan_id, 10);

        const items = [subscriptionItem];
        if (planAnswer && this._isPlanGiftEligible(planAnswer)) {
          subscriptionItem.properties = { '_includes_gift': planAnswer.free_gift_variant_id };
          // parent_id links the gift as a Shopify nested line when added in the same request
          const giftItem = {
            id: parseInt(planAnswer.free_gift_variant_id, 10),
            quantity: 1,
            parent_id: parseInt(dynamicVariantId, 10),
            properties: { '_is_free_gift': 'true', '_for_variant': dynamicVariantId.toString() },
          };
          if (planAnswer.free_gift_selling_plan_id) giftItem.selling_plan = parseInt(planAnswer.free_gift_selling_plan_id, 10);
          items.push(giftItem);
        }
        if (planAnswer && this._isPlanGift2Eligible(planAnswer)) {
          const giftItem2 = {
            id: parseInt(planAnswer.free_gift_2_variant_id, 10),
            quantity: 1,
            parent_id: parseInt(dynamicVariantId, 10),
            properties: { '_is_free_gift': 'true', '_for_variant': dynamicVariantId.toString() },
          };
          if (planAnswer.free_gift_2_selling_plan_id) giftItem2.selling_plan = parseInt(planAnswer.free_gift_2_selling_plan_id, 10);
          items.push(giftItem2);
        }
        for (const addon of Object.values(this._state.addons)) {
          items.push({ id: parseInt(addon.variant_id, 10), quantity: addon.quantity });
        }

        this._addToCart(items, { variant_id: dynamicVariantId, rule_name: '', quantity: 1 });
        return;
      }

      // Fallback: result block conditions
      const result = this._evaluateResults();
      if (!result) {
        this._showError('No matching product was found. Please contact us for help choosing the right box.');
        return;
      }
      if (!result.variant_id) {
        this._showError("This quiz isn't fully configured yet — no product has been assigned to your result.");
        return;
      }

      const subscriptionItem = { id: parseInt(result.variant_id, 10), quantity: parseInt(result.quantity, 10) || 1 };
      if (result.selling_plan_id) subscriptionItem.selling_plan = parseInt(result.selling_plan_id, 10);

      const items = [subscriptionItem];
      for (const addon of Object.values(this._state.addons)) {
        items.push({ id: parseInt(addon.variant_id, 10), quantity: addon.quantity });
      }

      this._addToCart(items, result);
    }

    // ── Cart add ──────────────────────────────────────────────────

    async _addToCart(items, result) {
      this._state.adding = true;
      if (this._btnFinish) {
        this._btnFinish.classList.add('is-loading');
        this._btnFinish.disabled = true;
      }

      document.dispatchEvent(
        new CustomEvent('maQuizComplete', {
          bubbles: true,
          detail: {
            selections: Object.assign({}, this._state.selections),
            matchedRule: result.rule_name || '',
            variantId: result.variant_id,
          },
        }),
      );

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'ma_quiz_complete',
          quiz_selections: Object.assign({}, this._state.selections),
          quiz_matched_rule: result.rule_name || '',
          quiz_variant_id: result.variant_id,
        });
      }

      try {
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.description || 'Something went wrong adding to cart. Please try again.');
        }

        const cartResponse = await resp.json();

        // Set order note from any message steps
        const orderNote = this._buildOrderNote();
        if (orderNote) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: orderNote }),
          }).catch(() => {});
        }

        // Update cart UI
        let _cartData = null;
        try {
          const cartResp = await fetch('/cart.js');
          _cartData = await cartResp.json();
          const cart = _cartData;


          // Only publish cartUpdate when NOT using openCartDrawer — the quiz-cart-drawer-open
          // event path already does a fullCartDrawerRefresh which triggers its own refresh and
          // handleGiftMilestones. Publishing here in that case creates a redundant concurrent
          // refresh that races the drawer's refresh and causes double-add of threshold gifts.
          if (this.dataset.openCartDrawer !== 'true') {
            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quiz-wizard', cartData: cart });
            }
          }
          const bubbleResp = await fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-icon-bubble`);
          const bubbleHtml = await bubbleResp.text();
          const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const newBubble = doc.getElementById('cart-icon-bubble');
          const curBubble = document.getElementById('cart-icon-bubble');
          if (newBubble && curBubble) curBubble.innerHTML = newBubble.innerHTML;
        } catch (_) {}

        document.dispatchEvent(
          new CustomEvent('maQuizCartAdded', {
            bubbles: true,
            detail: {
              selections: Object.assign({}, this._state.selections),
              variantId: result.variant_id,
              cartResponse,
            },
          }),
        );

        try {
          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'ma_quiz_cart_added',
              quiz_variant_id: result.variant_id,
              quiz_matched_rule: result.rule_name || '',
            });

            for (const [stepId, selected] of Object.entries(this._state.selections)) {
              const stepAnswers = this._answerMap[stepId] || [];
              for (const val of selected) {
                if (val === '__skipped__') continue;
                const answer = stepAnswers.find((a) => a.value === val);
                window.dataLayer.push({
                  event: 'ma_quiz_answer',
                  quiz_step: stepId,
                  quiz_value: val,
                  quiz_label: answer?.label || val,
                });
              }
            }
          }
        } catch (_) {}

        try {
          const payload = {
            selections: Object.assign({}, this._state.selections),
            matched_rule: result.rule_name || '',
            variant_id: result.variant_id,
          };
          if (window.klaviyo?.track) window.klaviyo.track('Quiz Completed', payload);
          else if (window._learnq) window._learnq.push(['track', 'Quiz Completed', payload]);
        } catch (_) {}

        this._state.completed = true;
        this._state.adding = false;

        // Clear remembered selections — refresh should show a fresh quiz, not the completed state
        try {
          localStorage.removeItem(this._storageKey);
        } catch (_) {}

        if (this.dataset.openCartDrawer === 'true') {
          let destination = window.routes?.root_url || '/';
          try {
            const ref = document.referrer;
            if (ref && ref !== window.location.href) {
              const refUrl = new URL(ref);
              if (refUrl.origin === window.location.origin) {
                destination = refUrl.pathname + refUrl.search + refUrl.hash;
              }
            }
          } catch (_) {}

          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            // Watch for the drawer being closed — then navigate to the previous page.
            let drawerOpened = false;
            const observer = new MutationObserver(() => {
              if (drawer.classList.contains('active')) {
                drawerOpened = true;
              } else if (drawerOpened) {
                observer.disconnect();
                window.location.href = destination;
              }
            });
            observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
          }

          document.dispatchEvent(new CustomEvent('ma:quiz-cart-drawer-open'));
          return;
        }

        const redirectUrl = this.dataset.redirectUrl;
        if (redirectUrl) {
          // Apply any applicable free gifts before navigating to checkout —
          // normally the cart drawer handles this, but we bypass it on direct redirect.
          if (_cartData) await this._applyPreCheckoutGifts(_cartData).catch(() => {});
          window.location.href = redirectUrl;
          return;
        }

        if (this._btnFinish) {
          this._btnFinish.classList.remove('is-loading');
          this._btnFinish.disabled = false;
        }
        this._showSuccessScreen();
        return;
      } catch (err) {
        console.error('[MAQuizWizard] Cart add error:', err);
        this._showError(err.message);
      }

      this._state.adding = false;
      if (this._btnFinish) {
        this._btnFinish.classList.remove('is-loading');
        this._btnFinish.disabled = false;
      }
    }

    // ── Pre-checkout gift application ─────────────────────────────
    // Mirrors handleGiftMilestones in ma-cart-drawer.js but without a drawer refresh.
    // Called when the wizard redirects directly to checkout so gifts are in the cart.

    async _applyPreCheckoutGifts(cartData) {
      const gifts = this._cartGifts || [];
      if (!gifts.length) return;

      const cartTotal = cartData.items_subtotal_price ?? cartData.total_price;
      const cartItems = cartData.items || [];

      for (const gift of gifts) {
        const vid = String(gift.variant_id);
        const autoGiftItem = cartItems.find(
          (item) =>
            String(item.variant_id) === vid &&
            item.properties &&
            item.properties._ma_auto_gift === 'true'
        );
        const autoGiftInCart = !!autoGiftItem;

        const paidGiftInCart = cartItems.some(
          (item) =>
            String(item.variant_id) === vid &&
            !(item.properties && item.properties._ma_auto_gift === 'true') &&
            item.final_price > 0
        );
        const alreadyFreeInCart = cartItems.some(
          (item) => String(item.variant_id) === vid && item.final_price === 0
        );
        const wouldCreateLoop =
          paidGiftInCart && !alreadyFreeInCart && gift.price > 0 && cartTotal - gift.price < gift.threshold;

        const shouldBeInCart = cartTotal >= gift.threshold && !wouldCreateLoop;

        if (shouldBeInCart && !autoGiftInCart) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: gift.variant_id, quantity: 1, properties: { _ma_auto_gift: 'true' } }),
          });
        } else if (!shouldBeInCart && autoGiftInCart) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: autoGiftItem.key, quantity: 0 }),
          });
        }
      }
    }

    // ── Error handling ────────────────────────────────────────────

    _showError(msg) {
      if (!this._errorEl) return;
      this._errorEl.textContent = msg;
      this._errorEl.hidden = false;
    }

    _hideError() {
      if (!this._errorEl) return;
      this._errorEl.hidden = true;
    }
  }

  customElements.define('ma-quiz-wizard', MAQuizWizard);
})();
